import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSwipeable } from "react-swipeable";
import "./App.css";
import logo from "./logo_white.png";
import avatars from "./avatars";
import confetti from "canvas-confetti";
import { storage, auth, db } from "./firebase";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { signInWithPopup, signInWithRedirect, getRedirectResult, browserPopupRedirectResolver, GoogleAuthProvider, OAuthProvider, signOut, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, signInWithCredential } from "firebase/auth";
import { SignInWithApple } from "@capacitor-community/apple-sign-in";
import {
  collection,
  addDoc,
  onSnapshot,
  updateDoc,
  doc,
  deleteDoc,
  setDoc,
  getDocs,
  query,
  where,
  increment
} from "firebase/firestore";

const hashPin = async (pin) => {
  const data = new TextEncoder().encode(pin);
  const buf = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("");
};

const DEV_MODE = false;

const SKIN_TONES = [
  { id: "light",       hex: "#FDDBB4", label: "Light"        },
  { id: "medium-light",hex: "#FFCC80", label: "Medium light" },
  { id: "medium",      hex: "#D4A574", label: "Medium"       },
  { id: "medium-dark", hex: "#A0724A", label: "Medium dark"  },
  { id: "dark",        hex: "#6B3F23", label: "Dark"         },
];
const DEFAULT_SKIN = "#FFCC80";
function applySkin(svg, skinHex) {
  if (!skinHex || skinHex === DEFAULT_SKIN) return svg;
  return svg.replaceAll(DEFAULT_SKIN, skinHex);
}

// Family IDs with access to the exclusive CR7 Beta Tester avatar
// Add your family ID(s) here — find it in Settings > Family Code
const TESTER_FAMILY_IDS = [
  "VZLax354geZGDQ7vI4WdpxCjokz2", // The Gearings - free until further notice
  "xd1ib3ac8UeX59wwgh0C8swp3NG2", // The Merediths - free until further notice
  "tmt20mzEc0aM4wcao7jF7H4fO2k1", // The Thwaites - free until further notice
];

function LoginScreen({ onParentLogin, onChildLogin, onEmailLogin }) {
  const savedFamilyId = localStorage.getItem("familyId") || "";
  const [view, setView] = useState("home");
  const [familyCode, setFamilyCode] = useState(savedFamilyId);
  const [children, setChildren] = useState([]);
  const [childrenLoading, setChildrenLoading] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [emailView, setEmailView] = useState("signin"); // "signin" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    if (!familyCode) return;
    setChildrenLoading(true);
    const q = query(collection(db, "children"), where("familyId", "==", familyCode));
    const unsub = onSnapshot(q, snap => {
      setChildren(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setChildrenLoading(false);
    });
    return unsub;
  }, [familyCode]);

  const handlePinDigit = async (digit) => {
    const next = pin + digit;
    setPin(next);
    if (next.length < 4) return;

    const child = children.find(c => c.id === selectedChild);
    const hash = await hashPin(next);
    if (hash === child?.pinHash) {
      onChildLogin(child.id, familyCode);
    } else {
      setError("Wrong PIN — try again");
      setTimeout(() => { setPin(""); setError(""); }, 900);
    }
  };

  const containerStyle = {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "#f5f6fa",
    padding: 24,
    fontFamily: "system-ui, -apple-system, sans-serif"
  };

  const btnStyle = {
    width: "100%",
    maxWidth: 280,
    padding: "14px 0",
    borderRadius: 14,
    border: "none",
    fontSize: 16,
    fontWeight: "600",
    cursor: "pointer",
    marginTop: 12
  };

  if (view === "home") return (
    <div style={containerStyle}>
      <div style={{ background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)", borderRadius: 24, padding: 24, marginBottom: 32 }}>
        <img src={logo} alt="Chore Ace" style={{ height: 120, display: "block" }} />
      </div>
      <button style={{ ...btnStyle, background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)", color: "#fff" }} onClick={() => setView("parentLogin")}>
        👨‍👩‍👧 Parent Login
      </button>
      <button style={{ ...btnStyle, background: "#fff", color: "#333", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }} onClick={() => setView(savedFamilyId ? "childSelect" : "childFamily")}>
        👶 Child Login
      </button>
    </div>
  );

  if (view === "parentLogin") return (
    <div style={containerStyle}>
      <div style={{ background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)", borderRadius: 20, padding: 16, marginBottom: 24 }}>
        <img src={logo} alt="Chore Ace" style={{ height: 80, display: "block" }} />
      </div>
      <div style={{ fontWeight: "700", fontSize: 18, marginBottom: 20 }}>Parent Login</div>

      {/* Google */}
      <button style={{ ...btnStyle, background: "#fff", color: "#333", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }} onClick={onParentLogin}>
        <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/><path fill="none" d="M0 0h48v48H0z"/></svg>
        Continue with Google
      </button>

      {/* Apple — all platforms */}
      <button style={{ ...btnStyle, background: "#000", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }} onClick={() => onParentLogin("apple")}>
        <svg width="20" height="20" viewBox="0 0 814 1000" fill="white"><path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105.2-57.8-155.6-127.4C46 405.1 18 251.1 50.4 153.3 66.1 103.8 105 42.8 162.8 11.1c51.1-28.1 106.6-40.8 162-40.8 66 0 118.4 33.6 163.2 33.6 43.1 0 110.7-42.5 168.7-42.5 28.8 0 107.3 5.1 165.6 64.7zm-93.7-81.8c-30.4-34.5-79.6-60.3-129.5-60.3-7.4 0-14.9.6-22.4 1.7 1.2-7 1.8-14 1.8-21 0-46.1-18.5-93.2-51.1-126.4C460.8 17.5 407.6 0 354.4 0c-7.1 0-14.2.6-21.3 1.9 0 76.1 28.3 144.8 79.6 194.9 49.6 48.5 116.3 75.9 184.7 75.9 7.8 0 15.7-.4 23.6-1.4L694.4 259.1z"/></svg>
        Continue with Apple
      </button>

      {/* Email */}
      <button style={{ ...btnStyle, background: "#f0f0f0", color: "#333" }} onClick={() => setView("email")}>
        ✉️ Continue with Email
      </button>

      <button style={{ marginTop: 16, background: "none", border: "none", color: "#999", fontSize: 13, cursor: "pointer" }} onClick={() => setView("home")}>
        ← Back
      </button>
    </div>
  );

  if (view === "email") return (
    <div style={containerStyle}>
      <div style={{ background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)", borderRadius: 20, padding: 16, marginBottom: 24 }}>
        <img src={logo} alt="Chore Ace" style={{ height: 80, display: "block" }} />
      </div>
      <div style={{ fontWeight: "700", fontSize: 18, marginBottom: 4 }}>{emailView === "signup" ? "Create Account" : "Sign In"}</div>
      <div style={{ fontSize: 13, color: "#999", marginBottom: 20 }}>{emailView === "signup" ? "Set up your parent account" : "Welcome back"}</div>

      {emailError && <div style={{ color: "red", fontSize: 13, marginBottom: 12, textAlign: "center" }}>{emailError}</div>}

      <input
        style={{ width: "100%", maxWidth: 280, padding: 12, borderRadius: 10, border: "1px solid #ddd", fontSize: 15, boxSizing: "border-box", marginBottom: 10 }}
        placeholder="Email address"
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
      />
      <input
        style={{ width: "100%", maxWidth: 280, padding: 12, borderRadius: 10, border: "1px solid #ddd", fontSize: 15, boxSizing: "border-box", marginBottom: 16 }}
        placeholder="Password"
        type="password"
        value={password}
        onChange={e => setPassword(e.target.value)}
      />

      <button style={{ ...btnStyle, background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)", color: "#fff" }} onClick={async () => {
        setEmailError("");
        try {
          let cred;
          if (emailView === "signup") {
            cred = await createUserWithEmailAndPassword(auth, email, password);
          } else {
            cred = await signInWithEmailAndPassword(auth, email, password);
          }
          onEmailLogin(cred.user);
        } catch (err) {
          const msgs = {
            "auth/email-already-in-use": "An account with this email already exists — try signing in instead.",
            "auth/invalid-email": "Please enter a valid email address.",
            "auth/weak-password": "Password must be at least 6 characters.",
            "auth/user-not-found": "No account found — try creating one.",
            "auth/wrong-password": "Incorrect password — try again.",
            "auth/invalid-credential": "Incorrect email or password — try again.",
          };
          setEmailError(msgs[err.code] || "Something went wrong — please try again.");
        }
      }}>
        {emailView === "signup" ? "Create Account" : "Sign In"}
      </button>

      <button style={{ marginTop: 12, background: "none", border: "none", color: "#ff6a00", fontSize: 13, cursor: "pointer", fontWeight: "600" }}
        onClick={() => { setEmailError(""); setEmailView(emailView === "signup" ? "signin" : "signup"); }}>
        {emailView === "signup" ? "Already have an account? Sign in" : "New here? Create an account"}
      </button>

      <button style={{ marginTop: 12, background: "none", border: "none", color: "#999", fontSize: 13, cursor: "pointer" }} onClick={() => setView("parentLogin")}>
        ← Back
      </button>
    </div>
  );

  if (view === "childFamily") return (
    <div style={containerStyle}>
      <div style={{ background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)", borderRadius: 20, padding: 16, marginBottom: 24 }}>
        <img src={logo} alt="Chore Ace" style={{ height: 80, display: "block" }} />
      </div>
      <div style={{ fontWeight: "700", fontSize: 18, marginBottom: 16 }}>Enter Family Code</div>
      <input
        style={{ width: "100%", maxWidth: 280, padding: 12, borderRadius: 10, border: "1px solid #ddd", fontSize: 16, boxSizing: "border-box" }}
        placeholder="Ask a parent for the code"
        value={familyCode}
        onChange={e => setFamilyCode(e.target.value)}
      />
      <button
        style={{ ...btnStyle, background: familyCode ? "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)" : "#ccc", color: "#fff" }}
        disabled={!familyCode}
        onClick={() => setView("childSelect")}
      >
        Next →
      </button>
      <button style={{ ...btnStyle, background: "none", color: "#999", boxShadow: "none", fontSize: 14 }} onClick={() => setView("home")}>
        ← Back
      </button>
    </div>
  );

  if (view === "childSelect") return (
    <div style={containerStyle}>
      <div style={{ background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)", borderRadius: 20, padding: 16, marginBottom: 24 }}>
        <img src={logo} alt="Chore Ace" style={{ height: 80, display: "block" }} />
      </div>
      <div style={{ fontWeight: "700", fontSize: 18, marginBottom: 16 }}>Who are you?</div>
      {childrenLoading && <div style={{ opacity: 0.5, fontSize: 14 }}>Loading...</div>}
      {!childrenLoading && children.length === 0 && <div style={{ opacity: 0.5, fontSize: 14 }}>No children found — check the family code</div>}
      {children.map(c => {
        const av = avatars.find(a => a.id === c.avatar);
        const skinHex = SKIN_TONES.find(s => s.id === c.avatarSkin)?.hex;
        return (
          <button
            key={c.id}
            style={{ ...btnStyle, background: "#fff", color: "#333", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
            onClick={() => { setSelectedChild(c.id); setView("childPin"); }}
          >
            {av
              ? <span dangerouslySetInnerHTML={{ __html: applySkin(av.svg, skinHex) }} style={{ width: 36, height: 36, borderRadius: "50%", overflow: "hidden", flexShrink: 0, display: "block" }} />
              : <span style={{ fontSize: 24 }}>👤</span>
            }
            {c.name}
          </button>
        );
      })}
      <button style={{ ...btnStyle, background: "none", color: "#999", boxShadow: "none", fontSize: 14 }} onClick={() => setView("childFamily")}>
        ← Back
      </button>
    </div>
  );

  if (view === "childPin") {
    const child = children.find(c => c.id === selectedChild);
    const pinAv = avatars.find(a => a.id === child?.avatar);
    const pinSkin = SKIN_TONES.find(s => s.id === child?.avatarSkin)?.hex;
    const dots = ["●","●","●","●"].map((d, i) => (
      <span key={i} style={{
        fontSize: 32,
        margin: "0 8px",
        color: pin.length > i ? "#1a1a1a" : "#ddd",
        transition: "color 0.15s ease, transform 0.15s ease",
        display: "inline-block",
        transform: pin.length === i + 1 ? "scale(1.3)" : "scale(1)"
      }}>{d}</span>
    ));
    return (
      <div style={containerStyle}>
        <div style={{ background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)", borderRadius: 20, padding: 16, marginBottom: 16 }}>
          <img src={logo} alt="Chore Ace" style={{ height: 80, display: "block" }} />
        </div>
        {pinAv
          ? <span dangerouslySetInnerHTML={{ __html: applySkin(pinAv.svg, pinSkin) }} style={{ width: 72, height: 72, borderRadius: "50%", overflow: "hidden", display: "block", marginBottom: 10, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }} />
          : <div style={{ fontSize: 48, marginBottom: 10 }}>👤</div>
        }
        <div style={{ fontWeight: "700", fontSize: 18 }}>Hi {child?.name}!</div>
        <div style={{ marginBottom: 20, opacity: 0.6, fontSize: 14 }}>Enter your 4-digit PIN</div>
        <div style={{ marginBottom: 24 }}>{dots}</div>
        {error && <div style={{ color: "#e53935", marginBottom: 12, fontWeight: "600" }}>{error}</div>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 72px)", gap: 12 }}>
          {[1,2,3,4,5,6,7,8,9,"",0,"⌫"].map((d, i) => (
            <button
              key={i}
              disabled={d === ""}
              onClick={() => {
                if (d === "⌫") { setPin(p => p.slice(0,-1)); setError(""); }
                else if (d !== "") handlePinDigit(String(d));
              }}
              style={{
                height: 72, borderRadius: 36, border: "none",
                background: d === "" ? "transparent" : "#fff",
                boxShadow: d === "" ? "none" : "0 2px 8px rgba(0,0,0,0.08)",
                fontSize: 22, fontWeight: "600", cursor: d === "" ? "default" : "pointer"
              }}
            >
              {d}
            </button>
          ))}
        </div>
        <button style={{ ...btnStyle, background: "none", color: "#999", boxShadow: "none", fontSize: 14, marginTop: 20 }} onClick={() => { setView("childSelect"); setPin(""); setError(""); }}>
          ← Back
        </button>
      </div>
    );
  }

  return null;
}

function SwipeItem({ chore, isDone, isApproved, complete, remove, mode, occurrenceDate }) {

  const [offsetX, setOffsetX] = useState(0);

const swipeHandlers = useSwipeable({

 onSwiping: (e) => {
  const resistance = 0.6;
  setOffsetX(e.deltaX * resistance);
},

  onSwipedLeft: () => {
    if (mode === "parent") {
      remove(chore, occurrenceDate);
    }
    setOffsetX(0);
  },

  onSwipedRight: () => {
    if (!isDone && mode === "child") {
      complete(chore.id);
    }
    setOffsetX(0);
  },

  trackMouse: true   // ✅ MOVE HERE
});


  return (
<div
  {...swipeHandlers}
  style={{
    padding: 16,
    marginTop: 10,
    borderRadius: 18,
    boxShadow: "0 8px 18px rgba(0,0,0,0.06)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 16,
    fontWeight: "500",
    cursor: "pointer",
    touchAction: "pan-y",

transform: `translateX(${offsetX}px) scale(${1 - Math.min(Math.abs(offsetX) / 500, 0.05)})`,

transition: `
  transform ${offsetX === 0 ? "0.25s cubic-bezier(0.22,1,0.36,1)" : "0s"},
  background-color 0.2s ease
`,

    backgroundColor:
      offsetX > 50 ? "#d4edda" :
      offsetX < -50 ? "#ffe3e3" :
      isApproved ? "#d4edda" :
      isDone ? "#fff9db" :
      "#fff",
    borderLeft: isDone ? `4px solid ${isApproved ? "#4caf50" : "#f59e0b"}` : "4px solid #eee"
  }}
>

      <span>{isDone ? (isApproved ? "💰" : "✅") : "⬜"} {chore.name}</span>
      <button
        onClick={e => { e.stopPropagation(); remove(chore, occurrenceDate); }}
        style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", opacity: 0.4, padding: "0 4px", lineHeight: 1 }}
        title="Delete chore"
      >🗑</button>
    </div>
  );
}
export default function App(){
const shootConfetti = () => {
  const myCanvas = document.createElement("canvas");

  myCanvas.style.position = "fixed";
  myCanvas.style.top = "0";
  myCanvas.style.left = "0";
  myCanvas.style.width = "100%";
  myCanvas.style.height = "100%";
  myCanvas.style.pointerEvents = "none";
  myCanvas.style.zIndex = "9999";

  document.body.appendChild(myCanvas);

  const myConfetti = confetti.create(myCanvas, {
    resize: true
  });

  myConfetti({
    particleCount: 200,
    spread: 120,
    startVelocity: 40,
    origin: { y: 0.5 }
  });

  setTimeout(() => {
    document.body.removeChild(myCanvas);
  }, 2000);
  }; 

const [tab, setTab] = useState("home");
const tabs = ["home", "calendar", "rewards", "money", "leagues"];

const [authLoading, setAuthLoading] = useState(true);
const [authRole, setAuthRole] = useState(null); // "parent" | "child" | null
const suppressAuthListener = useRef(false);
const mode = authRole === "parent" ? "parent" : "child";

const [chores, setChores] = useState([]);

const [name, setName] = useState("");
const [pay, setPay] = useState("");
const [date, setDate] = useState("");
const [type, setType] = useState("daily");
const [day, setDay] = useState("Mon");
const [photoRequired, setPhotoRequired] = useState(false);
const [lateDeduction, setLateDeduction] = useState(false);
const [showQuickAdd, setShowQuickAdd] = useState(false);

const [showModal, setShowModal] = useState(false);
const [active, setActive] = useState(null);
const [activeCompletionDate, setActiveCompletionDate] = useState(null);
const [submitting, setSubmitting] = useState(false);
const [deletePrompt, setDeletePrompt] = useState(null); // { id, name, type, occurrenceDate }

const [selectedFile, setSelectedFile] = useState(null);
const [message, setMessage] = useState("");
const [showTick, setShowTick] = useState(false);


const [children, setChildren] = useState([]);
const [activeChild, setActiveChild] = useState(null);
const [rewardsLoaded, setRewardsLoaded] = useState(false);

/* ✅ DERIVED DATA (AFTER STATES) */
const currentChildData =
  children.find(c => c.id === activeChild) || {};

const streak = currentChildData.streak || 0;
const lastCompleted = currentChildData.lastCompleted || null;
const streakFreezeUsed = currentChildData.streakFreezeUsed || null;
const currentMonth = new Date().toISOString().slice(0, 7); // e.g. "2026-06"
const freezeAvailable = streakFreezeUsed !== currentMonth;

const [editChildName, setEditChildName] = useState(false);
const [pinInput, setPinInput] = useState("");
const [showSubscribeModal, setShowSubscribeModal] = useState(false);
const [isSubscribed, setIsSubscribed] = useState(false);
const [userEmail, setUserEmail] = useState("");
const [checkoutLoading, setCheckoutLoading] = useState(false);
const [upgradeRequested, setUpgradeRequested] = useState(false);
const [familyOnboarded, setFamilyOnboarded] = useState(false);
const [familyMetaLoaded, setFamilyMetaLoaded] = useState(false);
const [childrenLoaded, setChildrenLoaded] = useState(false);
const [onboardingStep, setOnboardingStep] = useState(0); // 0=off, 1=welcome, 2=family name, 3=add child
const [onboardFamilyName, setOnboardFamilyName] = useState("");
const [onboardChildName, setOnboardChildName] = useState("");
const [onboardPin, setOnboardPin] = useState("");
const [onboardPin2, setOnboardPin2] = useState("");
const [onboardPinError, setOnboardPinError] = useState("");
const [showAddChild, setShowAddChild] = useState(false);
const [showAvatarPicker, setShowAvatarPicker] = useState(null); // child id
const [addChildName, setAddChildName] = useState("");
const [addChildPin, setAddChildPin] = useState("");
const [addChildPin2, setAddChildPin2] = useState("");
const [addChildError, setAddChildError] = useState("");
const [showShareChild, setShowShareChild] = useState(null); // { name, pin }
const [celebration, setCelebration] = useState(null); // { choreName, pay, choreId }
const [resetPinChild, setResetPinChild] = useState(null); // child id
const [resetPinVal, setResetPinVal] = useState("");
const [resetPinVal2, setResetPinVal2] = useState("");
const [resetPinError, setResetPinError] = useState("");
const [moneyViewChild, setMoneyViewChild] = useState(null); // override for money tab (parent)
const [showPayConfirm, setShowPayConfirm] = useState(false);
const [showSettings, setShowSettings] = useState(false);
const [showReviewPrompt, setShowReviewPrompt] = useState(false);
const [showReferralPrompt, setShowReferralPrompt] = useState(false);
const [codeCopied, setCodeCopied] = useState(false);
const [referralCode, setReferralCode] = useState("");
const [referralCount, setReferralCount] = useState(0);
const [referralCopied, setReferralCopied] = useState(false);
const [secondParentCode, setSecondParentCode] = useState("");
const [showSecondParentCode, setShowSecondParentCode] = useState(false);
const [showJoinFamily, setShowJoinFamily] = useState(false);
const [joinFamilyCode, setJoinFamilyCode] = useState("");
const [joinFamilyError, setJoinFamilyError] = useState("");
const [showSplitSetup, setShowSplitSetup] = useState(null); // childId
const [splitHouseholdName, setSplitHouseholdName] = useState("");
const [splitOtherName, setSplitOtherName] = useState("");
const [splitPin, setSplitPin] = useState("");
const [splitPin2, setSplitPin2] = useState("");
const [splitError, setSplitError] = useState("");
const [splitCode, setSplitCode] = useState("");
const [showSplitCode, setShowSplitCode] = useState(false);
const [showJoinSplit, setShowJoinSplit] = useState(false);
const [joinSplitCode, setJoinSplitCode] = useState("");
const [joinSplitPin, setJoinSplitPin] = useState("");
const [joinSplitPin2, setJoinSplitPin2] = useState("");
const [joinSplitName, setJoinSplitName] = useState("");
const [joinSplitError, setJoinSplitError] = useState("");
const [familyName, setFamilyName] = useState("");
const [editingFamilyName, setEditingFamilyName] = useState(false);
const [photoLightbox, setPhotoLightbox] = useState(null); // url string

const [leagues, setLeagues] = useState([]);
const [leagueMembers, setLeagueMembers] = useState([]);
const [leagueRequests, setLeagueRequests] = useState([]);
const [referralLeaderboard, setReferralLeaderboard] = useState([]);
const [showCreateLeague, setShowCreateLeague] = useState(false);
const [showJoinLeague, setShowJoinLeague] = useState(false);
const [leagueName, setLeagueName] = useState("");
const [joinCode, setJoinCode] = useState("");
const [joinChildId, setJoinChildId] = useState("");
const [selectedLeague, setSelectedLeague] = useState(null);
const [familyId, setFamilyId] = useState("");
const [editingChore, setEditingChore] = useState(null);
const [editedPay, setEditedPay] = useState("");
const [editedName, setEditedName] = useState("");
const claimedRewards = currentChildData.claimedRewards || [];

/* ✅ SWIPE HANDLER (FIXED) */
const tabHandlers = useSwipeable({
  onSwipedLeft: (e) => {
    if (e.velocity > 0.3 || e.deltaX < -50) {
      const currentIndex = tabs.indexOf(tab);
      if (currentIndex < tabs.length - 1) {
        setTab(tabs[currentIndex + 1]);
      }
    }
  },

  onSwipedRight: (e) => {
    if (e.velocity > 0.3 || e.deltaX > 50) {
      const currentIndex = tabs.indexOf(tab);
      if (currentIndex > 0) {
        setTab(tabs[currentIndex - 1]);
      }
    }
  },

  trackMouse: true   // ✅ ADD THIS
});                  // ✅ AND THIS

// ✅ NOW THIS IS VALID AGAIN
const rewards = [
  { streak: 3, name: "🍫 Treat" },
  { streak: 5, name: "🎬 Movie Night" },
  { streak: 7, name: "🍕 Pick Dinner" },
  { streak: 14, name: "🎮 Game Night" },
  { streak: 30, name: "🎁 Big Reward" }
];

const [editRewards, setEditRewards] = useState(false);
const [rewardInputs, setRewardInputs] = useState(rewards);

useEffect(() => {
  const session = sessionStorage.getItem("childSession");
  if (session) {
    const { childId, fid } = JSON.parse(session);
    setAuthRole("child");
    setFamilyId(fid);
    setActiveChild(childId);
    setAuthLoading(false);
    return;
  }
  const savedChildId = localStorage.getItem("childId");
  const savedFid = localStorage.getItem("familyId");
  if (savedChildId && savedFid) {
    sessionStorage.setItem("childSession", JSON.stringify({ childId: savedChildId, fid: savedFid }));
    setAuthRole("child");
    setFamilyId(savedFid);
    setActiveChild(savedChildId);
    setAuthLoading(false);
    return;
  }

  // Capture referral code from URL and persist it
  const params = new URLSearchParams(window.location.search);
  const refCode = params.get("ref");
  if (refCode) {
    localStorage.setItem("referredBy", refCode);
    window.history.replaceState({}, "", window.location.pathname);
  }

  // Handle return from Stripe checkout
  if (params.get("subscribed") === "true") {
    setMessage("🎉 Welcome to Chore Ace! Your subscription is active.");
    setTimeout(() => setMessage(""), 4000);
    window.history.replaceState({}, "", window.location.pathname);
  }

  const authTimeout = setTimeout(() => setAuthLoading(false), 5000);

  const unsub = onAuthStateChanged(auth, async user => {
    clearTimeout(authTimeout);
    if (suppressAuthListener.current) return;
    if (user) {
      setUserEmail(user.email || "");
      // Check if this parent is linked to another family
      const metaSnap = await new Promise(resolve => {
        const u = onSnapshot(doc(db, "meta", `parent_${user.uid}`), s => { u(); resolve(s); });
      });
      const linkedFamilyId = metaSnap.exists() ? metaSnap.data().linkedFamilyId : null;
      const effectiveFamilyId = linkedFamilyId || user.uid;
      setAuthRole("parent");
      setFamilyId(effectiveFamilyId);
      localStorage.setItem("familyId", effectiveFamilyId);
    } else {
      setAuthRole(null);
      setFamilyId("");
      setUserEmail("");
    }
    setAuthLoading(false);
  });

  return unsub;
}, []);

useEffect(() => {
  if (authRole === "parent" && familyId && childrenLoaded && familyMetaLoaded && children.length === 0 && onboardingStep === 0 && !familyOnboarded) {
    setOnboardingStep(1);
  }
}, [authRole, familyId, childrenLoaded, familyMetaLoaded, children.length, onboardingStep, familyOnboarded]);

useEffect(() => {
  if (!activeChild && children.length > 0) {
    setActiveChild(children[0].id);
  }
}, [children]);

useEffect(() => {
  const cleanOld = async () => {
    const _now = new Date();
    const _mon = new Date(_now);
    _mon.setDate(_now.getDate() - (_now.getDay() === 0 ? 6 : _now.getDay() - 1));
    _mon.setHours(0, 0, 0, 0);
    const cutoff = _mon.getTime(); // start of current week (Monday 00:00)

    const oldChores = chores.filter(c =>
      c.done &&
      c.approved &&
      c.completedAt &&
      c.completedAt < cutoff
    );

    for (let c of oldChores) {
      try {
        if (c.photoPath) {
          const fileRef = ref(storage, c.photoPath);
          await deleteObject(fileRef);
        }

        await deleteDoc(doc(db, "chores", c.id));
      } catch (err) {
        console.log("Cleanup error", err);
      }
    }
  };

  if (chores.length > 0) {
    cleanOld();
  }
}, [chores]);
useEffect(() => {
  if (!familyId) return;

  setChildrenLoaded(false);
  setFamilyMetaLoaded(false);
  setFamilyOnboarded(false);

  const unsubChildren = onSnapshot(collection(db, "children"), snap => {
    setChildren(
      snap.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(c => c.familyId === familyId)
    );
    setChildrenLoaded(true);
  });

  const unsubChores = onSnapshot(collection(db, "chores"), snap => {
    const familyChores = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(c => c.familyId === familyId);
    setChores(familyChores);
  });


const unsubRewards = onSnapshot(
  doc(db, "meta", `rewards_${familyId}`),
  (docSnap) => {
    if (docSnap.exists()) {
      setRewardInputs(docSnap.data().rewards || []);
    }
    setRewardsLoaded(true);
  }
);

const unsubFamily = onSnapshot(
  doc(db, "meta", `family_${familyId}`),
  async (docSnap) => {
    if (docSnap.exists()) {
      setIsSubscribed(docSnap.data().subscribed || false);
      setUpgradeRequested(docSnap.data().upgradeRequested || false);
      setFamilyName(docSnap.data().familyName || "");
      setFamilyOnboarded(docSnap.data().onboarded || false);
      setReferralCount(docSnap.data().referralCount || 0);
      if (docSnap.data().referralCode) {
        setReferralCode(docSnap.data().referralCode);
      } else {
        const code = familyId.slice(0, 8).toUpperCase();
        await setDoc(doc(db, "meta", `family_${familyId}`), { referralCode: code }, { merge: true });
        setReferralCode(code);
      }
    } else {
      const code = familyId.slice(0, 8).toUpperCase();
      await setDoc(doc(db, "meta", `family_${familyId}`), { referralCode: code }, { merge: true });
      setReferralCode(code);
    }
    setFamilyMetaLoaded(true);
  }
);

const unsubLeagues = onSnapshot(
  query(collection(db, "leagues"), where("ownerFamilyId", "==", familyId)),
  snap => setLeagues(snap.docs.map(d => ({ id: d.id, ...d.data() })))
);

const unsubLeagueMembers = onSnapshot(
  query(collection(db, "leagueMembers"), where("familyId", "==", familyId)),
  snap => setLeagueMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
);

const unsubLeagueRequests = onSnapshot(
  query(collection(db, "leagueRequests"), where("leagueOwnerFamilyId", "==", familyId), where("status", "==", "pending")),
  snap => setLeagueRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })))
);

// Referral leaderboard — fetch all families sorted by referralCount
getDocs(query(collection(db, "meta")))
  .then(snap => {
    const all = snap.docs
      .filter(d => d.id.startsWith("family_") && d.data().familyName)
      .map(d => ({ familyName: d.data().familyName, referralCount: d.data().referralCount || 0 }))
      .sort((a, b) => b.referralCount - a.referralCount);
    setReferralLeaderboard(all);
  });

return () => {
  unsubChildren();
  unsubChores();
  unsubRewards();
  unsubFamily();
  unsubLeagues();
  unsubLeagueMembers();
  unsubLeagueRequests();
};

}, [familyId]);
useEffect(() => {
  if (!familyId || !rewardsLoaded) return;

  setDoc(doc(db, "meta", `rewards_${familyId}`), {
    rewards: rewardInputs
  });

}, [rewardInputs, familyId, rewardsLoaded]);

  /* ADD CHORE */
const addChore = async () => {
  if (!name || !pay) return;

let chore = {
  name,
  pay: Number(pay),
  type,
  done: false,
  approved: false,
  suggested: mode === "child",
  createdBy: mode,
  childId: activeChild,
  familyId: familyId,
  photoRequired: mode === "parent" ? photoRequired : false,
  lateDeduction: mode === "parent" ? lateDeduction : false,
  createdAt: Date.now()
};


  const todayMidnight = new Date();
  todayMidnight.setHours(0, 0, 0, 0);

  if (type === "once") {
    const chosen = new Date(date);
    chore.date = (chosen < todayMidnight ? todayMidnight : chosen).getTime();
  }

  if (type === "weekly") {
    chore.day = day;
  }

  if (type === "fortnightly") {
    const chosen = date ? new Date(date).setHours(0,0,0,0) : todayMidnight.getTime();
    const startDate = chosen < todayMidnight.getTime() ? todayMidnight.getTime() : chosen;
    chore.startDate = startDate;
    chore.day = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][new Date(startDate).getDay()];
  }

  await addDoc(collection(db, "chores"), chore);

  if (mode === "child") {
    setMessage("💡 Chore suggested! Waiting for parent approval.");
  } else {
    setMessage("✅ Chore added!");
  }
  setTimeout(() => setMessage(""), 2500);

  setName("");
  setPay("");
  setDate("");
  setPhotoRequired(false);
  setLateDeduction(false);
};
    /* TODAY FILTER */
    const today = new Date();
    const todayStr = today.toDateString();
    const todayDay = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][today.getDay()];
    const _dow = today.getDay();
    const _mon = new Date(today);
    _mon.setDate(today.getDate() - (_dow === 0 ? 6 : _dow - 1));
    const mondayStr = _mon.toDateString();

// Week runs Mon–Sun (UK style)
const now = new Date();
const weekStart = new Date(now);
const dowOffset = now.getDay() === 0 ? 6 : now.getDay() - 1;
weekStart.setDate(now.getDate() - dowOffset);
weekStart.setHours(0,0,0,0);
const weekEnd = new Date(weekStart.getTime() + 7*24*60*60*1000);

const lastWeekStart = new Date(weekStart);
lastWeekStart.setDate(weekStart.getDate() - 7);

// Set of date strings (toDateString format) for Mon through today
const thisWeekDateStrings = new Set();
for (let d = new Date(weekStart); d <= now; d.setDate(d.getDate() + 1)) {
  thisWeekDateStrings.add(new Date(d).toDateString());
}

    const isDoneThisPeriod = (c) => {
      if (!c.completedDate) return c.done || false;
      const cd = new Date(c.completedDate);
      return cd >= weekStart && cd <= weekEnd;
    };

    const isApprovedThisPeriod = (c) => {
      if (c.type === "daily") return c.approvedDate === todayStr;
      if (c.type === "weekly" || c.type === "fortnightly") return c.approvedDate === mondayStr;
      return c.approved;
    };

    const fmtPay = (amount) => {
      const n = parseFloat(amount) || 0;
      if (n < 1) return `${Math.round(n * 100)}p`;
      return `£${n % 1 === 0 ? n : n.toFixed(2)}`;
    };

    // Calendar-aware versions — check against the specific calendar day, not just today
    const isDoneOnDay = (c, day) => {
      const dayStr = day.toDateString();
      if (c.type === "daily") return c.completedDate === dayStr;
      if (c.type === "weekly" || c.type === "fortnightly") {
        const dow = day.getDay();
        const mon = new Date(day);
        mon.setDate(day.getDate() - (dow === 0 ? 6 : dow - 1));
        return c.completedDate === mon.toDateString();
      }
      return c.done;
    };

    const isApprovedOnDay = (c, day) => {
      const dayStr = day.toDateString();
      if (c.type === "daily") return c.approvedDate === dayStr;
      if (c.type === "weekly" || c.type === "fortnightly") {
        const dow = day.getDay();
        const mon = new Date(day);
        mon.setDate(day.getDate() - (dow === 0 ? 6 : dow - 1));
        return c.approvedDate === mon.toDateString();
      }
      return c.approved;
    };

    const todayChores = chores.filter(c=>{
      if (c.suggested) return false;
      if (!activeChild) return true;
      if (c.childId !== activeChild) return false;
      if (c.skippedDates?.includes(todayStr)) return false;
      if(c.type==="daily") return true;
      if(c.type==="once"){
        return new Date(c.date).toDateString() === todayStr;
      }
      if(c.type==="weekly"){
        return c.day === todayDay;
      }
      if(c.type==="fortnightly"){
        if(c.day !== todayDay) return false;
        const start = c.startDate || 0;
        const diffDays = Math.round((today.setHours(0,0,0,0) - start) / (1000*60*60*24));
        return diffDays % 14 === 0;
      }
      return false;
    });
    todayChores.sort((a, b) => (a.type === "daily" ? -1 : 1) - (b.type === "daily" ? -1 : 1));

    /* 7 DAY VIEW */
    const weekDays = Array.from({length:14},(_,i)=>{
      const mon = new Date();
      const dow = mon.getDay();
      mon.setDate(mon.getDate() - (dow === 0 ? 6 : dow - 1)); // back to Monday
      mon.setDate(mon.getDate()+i);
      return mon;
    });

    /* ACTIONS */
    const complete = id => {
  setActive(id);
  setShowModal(true);
};

const finish = async (choreId, file) => {
  if (submitting) return;
  if (!choreId) return;
  setSubmitting(true);
  let photoURL = null;
  let photoPath = null;

  try {
  if (file) {
    const fileRef = ref(
      storage,
      `photos/${activeChild}/${Date.now()}_${file.name}`
    );
    await uploadBytes(fileRef, file);
    photoURL = await getDownloadURL(fileRef);
    photoPath = fileRef.fullPath;
  }

  // ✅ Save to Firestore
  const choreForDate = chores.find(c => c.id === choreId);
  const baseDate = activeCompletionDate || todayStr;
  const cDate = (choreForDate?.type === "weekly" || choreForDate?.type === "fortnightly") ? mondayStr : baseDate;
  await updateDoc(doc(db, "chores", choreId), {
    done: true,
    completedDate: cDate,
    photo: photoURL,
    photoPath: photoPath,
    completedAt: Date.now(),
    approved: false,
    approvedDate: null,
    approvedAmount: null
  });
// ✅ STREAK LOGIC
let newStreak = 1;

if (lastCompleted) {
  const last = new Date(lastCompleted).toDateString();

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  if (last === yesterday.toDateString()) {
    newStreak = streak + 1;
  } else if (last === todayStr) {
    newStreak = streak;
  }
}

await updateDoc(doc(db, "children", activeChild), {
  streak: newStreak,
  lastCompleted: Date.now()
});

  setShowModal(false);
  setSelectedFile(null);
  setSubmitting(false);
  setActiveCompletionDate(null);

  // Show celebration screen for child
  if (mode === "child") {
    const chore = chores.find(c => c.id === choreId);
    const childDoc = children.find(c => c.id === activeChild);
    setCelebration({
      choreName: chore?.name || "chore",
      pay: chore?.pay || 0,
      choreId,
      childName: childDoc?.name || "",
      avatar: childDoc?.avatar || null,
      avatarSkin: childDoc?.avatarSkin || null,
      streak: childDoc?.streak || 0,
    });
    shootConfetti();
  }
  } catch (err) {
    console.error("finish error:", err);
    setMessage("❌ Something went wrong — please try again");
    setTimeout(() => setMessage(""), 3000);
    setShowModal(false);
    setSelectedFile(null);
    setSubmitting(false);
    setActiveCompletionDate(null);
  }
};


 
const approve = async (id, late = false) => {
  const c = chores.find(ch => ch.id === id);
  if (!c) return;
  const choreChild = children.find(child => child.id === c.childId);
  if (!choreChild) return;
  const aDate = (c.type === "weekly" || c.type === "fortnightly") ? mondayStr : todayStr;
  const amount = late ? Math.round((c.pay || 0) * 0.75 * 100) / 100 : (c.pay || 0);
  await updateDoc(doc(db, "chores", id), { approved: true, approvedDate: aDate, approvedAmount: amount });
  await updateDoc(doc(db, "children", choreChild.id), { pending: increment(amount) });
  // Update weeklyPoints on all leagueMember docs for this child so cross-family leagues stay in sync
  const approvedThisWeek = chores.filter(ch =>
    ch.childId === c.childId && !ch.suggested && (ch.id === id || (ch.approved && (thisWeekDateStrings.has(ch.approvedDate) || isApprovedThisPeriod(ch))))
  ).length;
  const memberDocs = leagueMembers.filter(m => m.childId === c.childId);
  await Promise.all(memberDocs.map(m => updateDoc(doc(db, "leagueMembers", m.id), { weeklyPoints: approvedThisWeek * 10, weekStart: weekStart.toDateString() })));
  setMessage(late
    ? `⏰ Approved late — ${fmtPay(amount)} added (25% deducted) to ${choreChild.name}'s balance.`
    : `✅ Approved! ${fmtPay(amount)} added to ${choreChild.name}'s balance.`
  );
  setTimeout(() => setMessage(""), 2500);

  // Show review prompt after 5th approval
  const totalApproved = (parseInt(localStorage.getItem("totalApprovals") || "0")) + 1;
  localStorage.setItem("totalApprovals", totalApproved);
  if (totalApproved === 3 && !localStorage.getItem("referralPromptShown") && referralCount < 3) {
    setShowReferralPrompt(true);
    localStorage.setItem("referralPromptShown", "true");
  } else if (totalApproved === 5 && !localStorage.getItem("reviewPromptShown")) {
    setShowReviewPrompt(true);
    localStorage.setItem("reviewPromptShown", "true");
  }
};

const unapprove = async id => {
  const c = chores.find(ch => ch.id === id);
  if (!c) return;
  const choreChild = children.find(child => child.id === c.childId);
  if (!choreChild) return;
  await updateDoc(doc(db, "chores", id), { approved: false, approvedDate: null });
  await updateDoc(doc(db, "children", choreChild.id), {
    pending: increment(-(parseFloat(c.pay) || 0))
  });
  // Recalculate weeklyPoints after unapproval
  const approvedThisWeek = chores.filter(ch =>
    ch.childId === c.childId && !ch.suggested && ch.id !== id && ch.approved && (thisWeekDateStrings.has(ch.approvedDate) || isApprovedThisPeriod(ch))
  ).length;
  const memberDocs = leagueMembers.filter(m => m.childId === c.childId);
  await Promise.all(memberDocs.map(m => updateDoc(doc(db, "leagueMembers", m.id), { weeklyPoints: approvedThisWeek * 10, weekStart: weekStart.toDateString() })));
  setMessage(`↩️ Approval undone for ${choreChild.name}.`);
  setTimeout(() => setMessage(""), 2500);
};

const approveAll = async () => {
  const toApprove = chores.filter(c => c.done && !c.approved);
  for (const c of toApprove) {
    await approve(c.id);
  }
};

   const remove = async (id, occurrenceDate) => {
  const chore = chores.find(c => c.id === id);

  if (occurrenceDate) {
    // Skip just this one occurrence — add date to skippedDates array
    const existing = chore?.skippedDates || [];
    await updateDoc(doc(db, "chores", id), {
      skippedDates: [...existing, occurrenceDate]
    });
    return;
  }

  // Delete entire series
  if (chore?.photoPath) {
    try {
      const fileRef = ref(storage, chore.photoPath);
      await deleteObject(fileRef);
    } catch (err) {
      console.log("Photo already deleted or missing");
    }
  }

  await deleteDoc(doc(db, "chores", id));
};


    /* CALCS */
const total = chores.filter(c =>
  !c.suggested && (!activeChild || c.childId === activeChild) && c.type === "daily"
).length;

const done = chores.filter(c =>
  !c.suggested && (!activeChild || c.childId === activeChild) && c.type === "daily" && isDoneThisPeriod(c)
).length;

const progress = total ? Math.round((done / total) * 100) : 0;
 const earned = chores
  .filter(c => c.childId === activeChild && c.done)
  .reduce((s, c) => s + (parseFloat(c.pay) || 0), 0);
const effectiveMoneyChild = moneyViewChild || activeChild;

const weekDayNames = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const potentialLeft = chores
  .filter(c => !c.suggested && (!effectiveMoneyChild || c.childId === effectiveMoneyChild))
  .reduce((sum, c) => {
    const pay = parseFloat(c.pay) || 0;
    if (c.type === "daily") {
      const dow = now.getDay();
      const elapsed = dow === 0 ? 7 : dow;
      const daysLeft = (7 - elapsed) + (isDoneThisPeriod(c) ? 0 : 1);
      return sum + pay * daysLeft;
    }
    if (c.type === "weekly" || c.type === "fortnightly") {
      return isDoneThisPeriod(c) ? sum : sum + pay;
    }
    if (c.type === "once") {
      if (isDoneThisPeriod(c)) return sum;
      const d = new Date(c.date);
      return (d >= weekStart && d < weekEnd) ? sum + pay : sum;
    }
    return sum;
  }, 0);
 // ✅ ADD BADGE LOGIC HERE
    const getBadge = (streak) => {
      if (streak >= 30) return "👑";
      if (streak >= 7) return "🔥";
      if (streak >= 3) return "🏅";
      return "";
    };// 🏆 FAMILY LEADERBOARD
const leaderboard = children
  .map(child => {
    const points = chores
      .filter(c => c.childId === child.id && !c.suggested && c.approved && (thisWeekDateStrings.has(c.approvedDate) || isApprovedThisPeriod(c)))
      .reduce((sum, c) => sum + 10, 0);

       return {
      id: child.id,
      name: child.name,
      points,
      badge: getBadge(child.streak || 0) // ✅ ADD THIS LINE
    };
  })
  .sort((a, b) => b.points - a.points);

// ✅ only show if more than 1 child
const showLeaderboard = children.length > 1;


// ✅ THIS WEEK
const thisWeekEarned = chores
  .filter(c => {
    if (!c.done || !c.completedAt) return false;
    if (effectiveMoneyChild && c.childId !== effectiveMoneyChild) return false;
    const d = new Date(c.completedAt);
    return d >= weekStart && d < now;
  })
  .reduce((sum, c) => sum + (parseFloat(c.pay) || 0), 0);

// ✅ LAST WEEK
const lastWeekEarned = chores
  .filter(c => {
    if (!c.done || !c.completedAt) return false;
    if (effectiveMoneyChild && c.childId !== effectiveMoneyChild) return false;
    const d = new Date(c.completedAt);
    return d >= lastWeekStart && d < weekStart;
  })
  .reduce((sum, c) => sum + (parseFloat(c.pay) || 0), 0);


// Weekly progress: chores done this week vs total that should have been done by today
const daysElapsed = (() => {
  const day = now.getDay(); // 0=Sun
  return day === 0 ? 7 : day; // treat Sun as end of week (7 days elapsed)
})();

const weeklyDone = chores.filter(c => {
  if (c.suggested) return false;
  if (activeChild && c.childId !== activeChild) return false;
  return isDoneThisPeriod(c);
}).length;

const weeklyTotal = chores.filter(c => {
  if (c.suggested) return false;
  if (activeChild && c.childId !== activeChild) return false;
  if (c.type === "once") {
    const d = new Date(c.date);
    return d >= weekStart && d <= weekEnd;
  }
  return c.type === "daily" || c.type === "weekly" || c.type === "fortnightly";
}).length;

const weeklyProgress = weeklyTotal > 0 ? Math.round((weeklyDone / weeklyTotal) * 100) : 0;

const handleParentLogin = async (type = "google") => {
  suppressAuthListener.current = false;
  const isNative = window.Capacitor?.isNativePlatform?.();
  let result;
  try {
    if (type === "apple") {
      if (isNative) {
        const rawNonce = Array.from(crypto.getRandomValues(new Uint8Array(32)))
          .map(b => b.toString(16).padStart(2, "0")).join("");
        const hashBuffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(rawNonce));
        const hashedNonce = Array.from(new Uint8Array(hashBuffer))
          .map(b => b.toString(16).padStart(2, "0")).join("");
        const appleResult = await SignInWithApple.authorize({
          clientId: "com.choreace.app",
          redirectURI: "https://app.chore-ace.com",
          scopes: "email name",
          nonce: hashedNonce,
        });
        const { identityToken } = appleResult.response;
        const provider = new OAuthProvider("apple.com");
        const credential = provider.credential({ idToken: identityToken, rawNonce });
        result = await signInWithCredential(auth, credential);
      } else {
        const provider = new OAuthProvider("apple.com");
        provider.addScope("email");
        provider.addScope("name");
        result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
      }
    } else {
      if (isNative) {
        alert("Please use Apple Sign-In or Email to log in on the mobile app.");
        return;
      }
      const provider = new GoogleAuthProvider();
      result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
    }
    setAuthRole("parent");
    setFamilyId(result.user.uid);
    localStorage.setItem("familyId", result.user.uid);
    localStorage.removeItem("childId");
  } catch (err) {
    if (err.code !== "auth/popup-closed-by-user" && err.message !== "The user closed the sign in prompt.") {
      alert("Sign-in failed: " + (err.message || err));
    }
  }
};

const handleChildLogin = (childId, fid) => {
  sessionStorage.setItem("childSession", JSON.stringify({ childId, fid }));
  localStorage.setItem("familyId", fid);
  localStorage.setItem("childId", childId);
  setAuthRole("child");
  setFamilyId(fid);
  setActiveChild(childId);
};

const handleSignOut = async () => {
  if (authRole === "parent") {
    suppressAuthListener.current = false;
    await signOut(auth);
    setAuthRole(null);
    setFamilyId("");
    setActiveChild(null);
  } else {
    // Child switching back to login — suppress the auth listener so Firebase
    // parent session doesn't immediately re-login as parent
    suppressAuthListener.current = true;
    sessionStorage.removeItem("childSession");
    localStorage.removeItem("childId");
    setAuthRole(null);
    setActiveChild(null);
  }
};

const generateCode = () => Math.random().toString(36).substring(2, 8).toUpperCase();

const createLeague = async () => {
  if (!leagueName.trim()) return;
  const code = generateCode();
  const leagueRef = await addDoc(collection(db, "leagues"), {
    name: leagueName.trim(),
    ownerFamilyId: familyId,
    ownerChildId: mode === "child" ? activeChild : null,
    inviteCode: code,
    createdAt: Date.now()
  });
  // auto-add the creator's child as a member
  const child = children.find(c => c.id === activeChild);
  if (child) {
    await addDoc(collection(db, "leagueMembers"), {
      leagueId: leagueRef.id,
      leagueName: leagueName.trim(),
      leagueOwnerFamilyId: familyId,
      childId: child.id,
      childName: child.name,
      familyId,
      joinedAt: Date.now()
    });
  }
  setLeagueName("");
  setShowCreateLeague(false);
};

const requestJoinLeague = async () => {
  const targetChildId = mode === "child" ? activeChild : joinChildId;
  if (!joinCode.trim() || !targetChildId) return;
  const q = query(collection(db, "leagues"), where("inviteCode", "==", joinCode.trim().toUpperCase()));
  const snap = await new Promise(resolve => {
    const unsub = onSnapshot(q, s => { unsub(); resolve(s); });
  });
  if (snap.empty) { alert("Code not found — check with the league owner"); return; }
  const leagueDoc = snap.docs[0];
  const league = { id: leagueDoc.id, ...leagueDoc.data() };

  // Block if this child is already a member
  const alreadyMember = leagueMembers.some(m => m.leagueId === league.id && m.childId === targetChildId);
  if (alreadyMember) { alert("This child is already in that league!"); return; }

  const child = children.find(c => c.id === targetChildId);
  await addDoc(collection(db, "leagueRequests"), {
    leagueId: league.id,
    leagueName: league.name,
    leagueOwnerFamilyId: league.ownerFamilyId,
    childId: targetChildId,
    childName: child?.name || "Unknown",
    familyId,
    status: "pending",
    requestedAt: Date.now()
  });
  setJoinCode("");
  setJoinChildId("");
  setShowJoinLeague(false);
  alert(`Request sent to join "${league.name}"! Waiting for approval.`);
};

const approveLeagueRequest = async (req) => {
  await addDoc(collection(db, "leagueMembers"), {
    leagueId: req.leagueId,
    leagueName: req.leagueName,
    leagueOwnerFamilyId: req.leagueOwnerFamilyId,
    childId: req.childId,
    childName: req.childName,
    familyId: req.familyId,
    joinedAt: Date.now()
  });
  await updateDoc(doc(db, "leagueRequests", req.id), { status: "approved" });
};

const rejectLeagueRequest = async (req) => {
  await updateDoc(doc(db, "leagueRequests", req.id), { status: "rejected" });
};

const removeFromLeague = async (memberId) => {
  await deleteDoc(doc(db, "leagueMembers", memberId));
};

const deleteLeague = async (leagueId) => {
  if (!window.confirm("Delete this league? All members will be removed and this cannot be undone.")) return;
  // Delete all members
  const membersSnap = await new Promise(resolve => {
    const unsub = onSnapshot(query(collection(db, "leagueMembers"), where("leagueId", "==", leagueId)), s => { unsub(); resolve(s); });
  });
  for (const m of membersSnap.docs) await deleteDoc(doc(db, "leagueMembers", m.id));
  // Delete all requests
  const reqSnap = await new Promise(resolve => {
    const unsub = onSnapshot(query(collection(db, "leagueRequests"), where("leagueId", "==", leagueId)), s => { unsub(); resolve(s); });
  });
  for (const r of reqSnap.docs) await deleteDoc(doc(db, "leagueRequests", r.id));
  // Delete the league
  await deleteDoc(doc(db, "leagues", leagueId));
  setMessage("🗑 League deleted.");
  setTimeout(() => setMessage(""), 2500);
};

// Split household — create split code for a child
const setupSplitHousehold = async (childId) => {
  if (!splitHouseholdName.trim() || !splitOtherName.trim()) { setSplitError("Please name both households"); return; }
  if (splitPin.length !== 4) { setSplitError("PIN must be 4 digits"); return; }
  if (splitPin !== splitPin2) { setSplitError("PINs don't match"); return; }
  setSplitError("");
  const code = Math.random().toString(36).substring(2, 8).toUpperCase();
  const hash = await hashPin(splitPin);
  await updateDoc(doc(db, "children", childId), {
    splitCode: code,
    splitHouseholdName: splitHouseholdName.trim(),
    splitOtherName: splitOtherName.trim(),
    splitPinHash: hash
  });
  setSplitCode(code);
  setShowSplitSetup(null);
  setShowSplitCode(true);
  setSplitHouseholdName("");
  setSplitOtherName("");
  setSplitPin("");
  setSplitPin2("");
};

// Split household — second parent links child using split code
const joinSplitHousehold = async () => {
  if (!joinSplitCode.trim()) { setJoinSplitError("Enter the split code"); return; }
  if (joinSplitPin.length !== 4) { setJoinSplitError("PIN must be 4 digits"); return; }
  if (joinSplitPin !== joinSplitPin2) { setJoinSplitError("PINs don't match"); return; }
  setJoinSplitError("");
  const q = query(collection(db, "children"), where("splitCode", "==", joinSplitCode.trim().toUpperCase()));
  const snap = await new Promise(resolve => { const unsub = onSnapshot(q, s => { unsub(); resolve(s); }); });
  if (snap.empty) { setJoinSplitError("Code not found — check with the other parent"); return; }
  const originalChild = snap.docs[0];
  const originalData = originalChild.data();
  const hash = await hashPin(joinSplitPin);
  await addDoc(collection(db, "children"), {
    name: joinSplitName.trim() || originalData.name,
    familyId,
    pending: 0,
    balance: 0,
    streak: 0,
    lastCompleted: null,
    leagueId: null,
    inviteCode: null,
    avatar: originalData.avatar || null,
    pinHash: hash,
    splitLinkedId: originalChild.id,
    splitHouseholdName: originalData.splitOtherName || "This household",
  });
  setShowJoinSplit(false);
  setJoinSplitCode("");
  setJoinSplitPin("");
  setJoinSplitPin2("");
  setJoinSplitName("");
  setMessage("✅ Split household set up! Child added to your app.");
  setTimeout(() => setMessage(""), 3000);
};

const handleUpgradeRequest = async () => {
  if (!familyId) return;
  await setDoc(doc(db, "meta", `family_${familyId}`), { upgradeRequested: true }, { merge: true });
  setUpgradeRequested(true);
};

const handleSubscribe = async () => {
  if (DEV_MODE) {
    await setDoc(doc(db, "meta", `family_${familyId}`), { subscribed: true }, { merge: true });
    setIsSubscribed(true);
    setShowSubscribeModal(false);
    setMessage("🎉 Subscribed! Full access unlocked.");
    setTimeout(() => setMessage(""), 3000);
    return;
  }
  setCheckoutLoading(true);
  try {
    const res = await fetch("https://us-central1-chore-champ-d474f.cloudfunctions.net/createCheckoutSession", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ familyId, email: userEmail, referredBy: localStorage.getItem("referredBy") || null }),
    });
    const data = await res.json();
    if (data.url) {
      window.location.href = data.url;
    } else {
      alert("Something went wrong. Please try again.");
    }
  } catch (err) {
    alert("Could not connect to payment. Please try again.");
  } finally {
    setCheckoutLoading(false);
  }
};

const generateSecondParentCode = async () => {
  const code = familyId.slice(0, 6).toUpperCase() + Math.random().toString(36).slice(2, 4).toUpperCase();
  await setDoc(doc(db, "meta", `family_${familyId}`), { secondParentCode: code }, { merge: true });
  setSecondParentCode(code);
  setShowSecondParentCode(true);
};

const joinFamilyAsSecondParent = async () => {
  if (!joinFamilyCode.trim()) { setJoinFamilyError("Enter the code"); return; }
  const code = joinFamilyCode.trim().toUpperCase();
  // Find the family with this code
  const snap = await new Promise(resolve => {
    const unsub = onSnapshot(query(collection(db, "meta"), where("secondParentCode", "==", code)), s => { unsub(); resolve(s); });
  });
  if (snap.empty) { setJoinFamilyError("Code not found — check with the other parent"); return; }
  const familyDoc = snap.docs[0];
  const linkedId = familyDoc.id.replace("family_", "");
  // Store the link on this parent's meta
  const currentUser = auth.currentUser;
  await setDoc(doc(db, "meta", `parent_${currentUser.uid}`), { linkedFamilyId: linkedId }, { merge: true });
  setMessage("✅ Joined family! Reloading...");
  setTimeout(() => window.location.reload(), 1500);
};

const useStreakFreeze = async () => {
  if (!freezeAvailable) { setMessage("❄️ You've already used your freeze this month!"); setTimeout(() => setMessage(""), 2500); return; }
  await updateDoc(doc(db, "children", activeChild), { streakFreezeUsed: currentMonth, lastCompleted: Date.now() });
  setMessage("🧊 Streak frozen! Your streak is safe for today.");
  setTimeout(() => setMessage(""), 3000);
};

const finishOnboarding = async () => {
  if (!familyId) { setOnboardPinError("Not logged in — please try again"); return; }
  if (onboardPin.length !== 4) { setOnboardPinError("PIN must be 4 digits"); return; }
  if (onboardPin !== onboardPin2) { setOnboardPinError("PINs don't match"); return; }
  if (!onboardChildName.trim()) return;
  setOnboardPinError("");
  setFamilyOnboarded(true);

  // Save family name + onboarded flag to Firestore so it persists across cache clears
  const referredBy = localStorage.getItem("referredBy") || null;
  await setDoc(doc(db, "meta", `family_${familyId}`), {
    familyName: onboardFamilyName.trim() || "",
    onboarded: true,
    ...(referredBy ? { referredBy } : {})
  }, { merge: true });

  // Add first child
  const hash = await hashPin(onboardPin);
  const docRef = await addDoc(collection(db, "children"), {
    name: onboardChildName.trim(),
    pending: 0,
    balance: 0,
    familyId,
    streak: 0,
    lastCompleted: null,
    leagueId: null,
    inviteCode: null,
    pinHash: hash
  });
  setActiveChild(docRef.id);
  setOnboardingStep(4);
};

const addChild = () => {
  if (children.length >= 1 && !isSubscribed && !DEV_MODE) {
    setShowSubscribeModal(true);
    return;
  }
  setAddChildName("");
  setAddChildPin("");
  setAddChildPin2("");
  setAddChildError("");
  setShowAddChild(true);
};

const confirmAddChild = async () => {
  if (!addChildName.trim()) { setAddChildError("Please enter a name"); return; }
  if (addChildPin.length !== 4) { setAddChildError("PIN must be 4 digits"); return; }
  if (addChildPin !== addChildPin2) { setAddChildError("PINs don't match — try again"); return; }
  setAddChildError("");
  try {
    const hash = await hashPin(addChildPin);
    const docRef = await addDoc(collection(db, "children"), {
      name: addChildName.trim(),
      pending: 0,
      balance: 0,
      familyId,
      streak: 0,
      lastCompleted: null,
      leagueId: null,
      inviteCode: null,
      pinHash: hash
    });
    setActiveChild(docRef.id);
    setShowAddChild(false);
    setShowShareChild({ name: addChildName.trim(), pin: addChildPin });
  } catch (err) {
    setAddChildError("Something went wrong — please try again");
    console.error("addChild error:", err);
  }
};
  
const currentChild = children.find(c => c.id === activeChild) || null;
  
   const nextReward = rewardInputs.find(r => r.streak > streak);
   const remaining = nextReward ? nextReward.streak - streak : null;
const styles = {

barBg: {
  width: "100%",
  height: 10,
  background: "#eee",
  borderRadius: 6,
  overflow: "hidden",
},

bar: {
  height: "100%",
  background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)"
},

button: {
  background: "linear-gradient(135deg, #ff4000, #ff9500)",
  color: "#fff",
  border: "none",
  padding: "12px 20px",
  borderRadius: 12,
  cursor: "pointer",
  marginTop: 10,
  fontWeight: "800",
  fontSize: 15,
  boxShadow: "0 4px 14px rgba(111,212,0,0.5)",
  width: "auto",
  alignSelf: "flex-start"
},

  row: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    padding: "12px 16px",
    boxSizing: "border-box",
    background: "rgba(255,255,255,0.6)",
    borderRadius: "0 0 16px 16px",
    marginBottom: 4
  },


   reportCard: {
  background: "#ffffff",
  padding: 16,
  marginTop: 16,
  borderRadius: 16,
  boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
  
  display: "block",        // ✅ CRITICAL CHANGE
  width: "100%"            // ✅ ensure full width
},

reportRow: {
  display: "flex",
  alignItems: "center",
  fontSize: 15,
  width: "100%",
},



  noChores: {
  opacity: 0.6,
  fontSize: 14,
  padding: "6px 0"
},

dayCard: {
  background: "#ffffff",
  padding: 16,
  marginTop: 16,
  borderRadius: 16,
  border: "1px solid #e5e7eb",
  boxShadow: "0 6px 16px rgba(0,0,0,0.08)"
},

nav: {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 24px",
  background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)",
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  boxShadow: "0 -4px 20px rgba(255,106,0,0.8), 0 0 40px rgba(255,106,0,0.4), inset 0 1px 0 rgba(255,255,255,0.25)",
  zIndex: 20
},


  
navButton: {
    background: "none",
    border: "none",
    fontSize: 24,
    cursor: "pointer",
    transition: "transform 0.2s ease",
    filter: "brightness(0) invert(1)",
    opacity: 0.75,
    padding: "4px 8px",
    borderRadius: 10
  },

  activeTab: {
    transform: "scale(1.25)",
    opacity: 1,
    background: "rgba(255,255,255,0.25)",
    borderRadius: 12,
    filter: "brightness(0) invert(1)"
  },

container: {
  width: "100%",
  maxWidth: "100vw",
  overflowX: "hidden",
  margin: "0 auto",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  fontFamily: "system-ui, -apple-system, sans-serif",
  background: "linear-gradient(160deg, #fff8e1 0%, #f3e5f5 50%, #e8f5e9 100%)"
},
  title: {
    textAlign: "center",
    fontWeight: "600",
    marginBottom: 10,
  },

card: {
  background: "#fff",
  padding: 16,
  width: "100%",
  maxWidth: "100%",
  alignSelf: "stretch",
  boxSizing: "border-box",
  marginTop: 10,
  borderRadius: 18,
  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
  borderLeft: "5px solid linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: 16,
  fontWeight: "500",
  transition: "transform 0.15s ease, box-shadow 0.15s ease"
},

cardColumn: {
  background: "#fff",
  paddingTop: 14,
  paddingBottom: 14,
  paddingLeft: 16,
  paddingRight: 16,
  overflow: "visible",
  width: "100%",
  maxWidth: "100%",
  alignSelf: "stretch",
  boxSizing: "border-box",
  marginTop: 12,
  borderRadius: 16,
  boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
  borderLeft: "5px solid linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)",
  display: "flex",
  flexDirection: "column",
  gap: 6,
  alignItems: "stretch"
},

  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999,
    animation: "fadeIn 0.2s ease",
    paddingBottom: 70,
    paddingTop: 20,
    boxSizing: "border-box"
  },

  modal: {
  background: "#fff",
  padding: 20,
  borderRadius: 16,
  width: "90%",
  maxWidth: 360,
  maxHeight: "70vh",
  overflowY: "auto",
  boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
  display: "flex",
  flexDirection: "column",
  gap: 10,
  animation: "popIn 0.25s ease-out"
}
};


if (authLoading) return (
  <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f6fa" }}>
    <img src={logo} alt="Loading" style={{ height: 100, opacity: 0.6 }} />
  </div>
);

if (!authRole) return (
  <LoginScreen onParentLogin={handleParentLogin} onChildLogin={handleChildLogin} onEmailLogin={(user) => {
    suppressAuthListener.current = false;
    setAuthRole("parent");
    setFamilyId(user.uid);
    localStorage.setItem("familyId", user.uid);
    localStorage.removeItem("childId");
  }} />
);

if (onboardingStep > 0) {
  const stepDots = [1,2,3,4].map(i => (
    <div key={i} style={{
      width: 8, height: 8, borderRadius: "50%",
      background: onboardingStep >= i ? "#ff6a00" : "#ddd",
      transition: "background 0.3s"
    }} />
  ));

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(160deg, #fff8e1 0%, #f3e5f5 50%, #e8f5e9 100%)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: 24, fontFamily: "inherit"
    }}>
      {/* Progress dots */}
      <div style={{ display: "flex", gap: 8, marginBottom: 32 }}>{stepDots}</div>

      {/* STEP 1 — Welcome */}
      {onboardingStep === 1 && (
        <div style={{ textAlign: "center", animation: "fadeIn 0.4s ease" }}>
          <div style={{ fontSize: 64, marginBottom: 12 }}>🏆</div>
          <div style={{ fontSize: 26, fontWeight: "800", marginBottom: 8 }}>Welcome to Chore Ace!</div>
          <div style={{ fontSize: 15, opacity: 0.6, marginBottom: 32, maxWidth: 280 }}>
            Let's get your family set up in less than a minute.
          </div>
          <div style={{ marginBottom: 16, textAlign: "left" }}>
            <div style={{ fontSize: 14, fontWeight: "600", marginBottom: 6, opacity: 0.7 }}>
              What's your family name? (optional)
            </div>
            <input
              placeholder="e.g. The Smiths"
              value={onboardFamilyName}
              onChange={e => setOnboardFamilyName(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box", fontSize: 16, padding: "12px 14px", borderRadius: 12, border: "1.5px solid #ddd" }}
            />
          </div>
          <button
            onClick={() => setOnboardingStep(2)}
            style={{
              width: "100%", padding: "14px", borderRadius: 14, border: "none",
              background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)",
              color: "#fff", fontWeight: "800", fontSize: 16, cursor: "pointer"
            }}
          >
            Let's go! 🚀
          </button>
        </div>
      )}

      {/* STEP 2 — How it works */}
      {onboardingStep === 2 && (
        <div style={{ textAlign: "center", animation: "fadeIn 0.4s ease", maxWidth: 320 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⚡</div>
          <div style={{ fontSize: 22, fontWeight: "800", marginBottom: 20 }}>Here's how it works</div>
          {[
            ["📋", "You set chores and how much each one pays"],
            ["✅", "Kids complete them and mark them done"],
            ["💰", "You approve and their balance goes up"],
            ["🎁", "Kids save up for rewards you set"],
          ].map(([icon, text]) => (
            <div key={text} style={{
              display: "flex", alignItems: "center", gap: 14,
              background: "#fff", borderRadius: 12, padding: "12px 16px",
              marginBottom: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              textAlign: "left"
            }}>
              <span style={{ fontSize: 24 }}>{icon}</span>
              <span style={{ fontSize: 14, fontWeight: "500" }}>{text}</span>
            </div>
          ))}
          <button
            onClick={() => setOnboardingStep(3)}
            style={{
              width: "100%", padding: "14px", borderRadius: 14, border: "none", marginTop: 8,
              background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)",
              color: "#fff", fontWeight: "800", fontSize: 16, cursor: "pointer"
            }}
          >
            Add your first child 👧
          </button>
        </div>
      )}

      {/* STEP 3 — Add first child */}
      {onboardingStep === 3 && (
        <div style={{ width: "100%", maxWidth: 320, animation: "fadeIn 0.4s ease" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>👧</div>
            <div style={{ fontSize: 22, fontWeight: "800" }}>Add your first child</div>
            <div style={{ fontSize: 14, opacity: 0.6, marginTop: 6 }}>They'll use a 4-digit PIN to log in</div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 6, opacity: 0.7 }}>Child's name</div>
            <input
              placeholder="e.g. Emma"
              value={onboardChildName}
              onChange={e => setOnboardChildName(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box", fontSize: 16, padding: "12px 14px", borderRadius: 12, border: "1.5px solid #ddd" }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 6, opacity: 0.7 }}>Choose a 4-digit PIN</div>
            <input
              placeholder="• • • •"
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={onboardPin}
              onChange={e => setOnboardPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              style={{ width: "100%", boxSizing: "border-box", fontSize: 20, letterSpacing: 8, padding: "12px 14px", borderRadius: 12, border: "1.5px solid #ddd", textAlign: "center" }}
            />
          </div>

          <div style={{ marginBottom: 4 }}>
            <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 6, opacity: 0.7 }}>Confirm PIN</div>
            <input
              placeholder="• • • •"
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={onboardPin2}
              onChange={e => setOnboardPin2(e.target.value.replace(/\D/g, "").slice(0, 4))}
              style={{ width: "100%", boxSizing: "border-box", fontSize: 20, letterSpacing: 8, padding: "12px 14px", borderRadius: 12, border: "1.5px solid #ddd", textAlign: "center" }}
            />
          </div>

          {onboardPinError && (
            <div style={{ color: "#e53935", fontSize: 13, marginBottom: 8, textAlign: "center" }}>{onboardPinError}</div>
          )}

          <button
            onClick={finishOnboarding}
            disabled={!onboardChildName.trim() || onboardPin.length !== 4 || onboardPin2.length !== 4}
            style={{
              width: "100%", padding: "14px", borderRadius: 14, border: "none", marginTop: 12,
              background: (!onboardChildName.trim() || onboardPin.length !== 4 || onboardPin2.length !== 4)
                ? "#ccc" : "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)",
              color: "#fff", fontWeight: "800", fontSize: 16,
              cursor: (!onboardChildName.trim() || onboardPin.length !== 4 || onboardPin2.length !== 4) ? "not-allowed" : "pointer"
            }}
          >
            All done! Let's start 🎉
          </button>

          <button
            onClick={() => setOnboardingStep(2)}
            style={{ background: "none", border: "none", color: "#999", cursor: "pointer", width: "100%", marginTop: 12, fontSize: 14 }}
          >
            ← Back
          </button>
        </div>
      )}

      {/* STEP 4 — Add first chore */}
      {onboardingStep === 4 && (
        <div style={{ textAlign: "center", animation: "fadeIn 0.4s ease", maxWidth: 320 }}>
          <div style={{ fontSize: 56, marginBottom: 12 }}>🎉</div>
          <div style={{ fontSize: 22, fontWeight: "800", marginBottom: 8 }}>
            {onboardChildName || "Your child"} is ready!
          </div>
          <div style={{ fontSize: 14, opacity: 0.6, marginBottom: 24, maxWidth: 260, margin: "0 auto 24px" }}>
            Now add your first chore so they know what to do. You can always add more later.
          </div>
          {[
            ["🛏️", "Make your bed", "1.00"],
            ["🍽️", "Wash the dishes", "1.50"],
            ["🧹", "Tidy your room", "2.00"],
          ].map(([icon, choreName, amount]) => (
            <div
              key={choreName}
              onClick={() => { setOnboardingStep(0); }}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "#fff", borderRadius: 12, padding: "12px 16px",
                marginBottom: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                cursor: "pointer", textAlign: "left"
              }}
            >
              <span style={{ fontSize: 14, fontWeight: "600" }}>{icon} {choreName}</span>
              <span style={{ fontSize: 13, color: "#ff6a00", fontWeight: "700" }}>£{amount}</span>
            </div>
          ))}
          <button
            onClick={() => setOnboardingStep(0)}
            style={{
              width: "100%", padding: "14px", borderRadius: 14, border: "none", marginTop: 8,
              background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)",
              color: "#fff", fontWeight: "800", fontSize: 16, cursor: "pointer"
            }}
          >
            Go to the app 🚀
          </button>
          <div style={{ fontSize: 12, opacity: 0.45, marginTop: 12 }}>
            You can add chores from the home screen any time
          </div>
        </div>
      )}

      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}

return (
  <div>

    <style>
      {`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes popIn {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}
    </style>

    {/* ✅ MAIN CONTAINER (CRITICAL FIX) */}
    <div style={styles.container}>

 


{/* HEADER */}
<div style={{
  position: "sticky",
  top: 0,
  zIndex: 10,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "6px 16px",
  background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)",
  boxShadow: "0 4px 20px rgba(255,106,0,0.8), 0 0 40px rgba(255,106,0,0.4), inset 0 1px 0 rgba(255,255,255,0.35)"
}}>
  <img
    src={logo}
    alt="Chore Ace"
    style={{ height: "200px", width: "auto", flex: 1, objectFit: "contain" }}
  />
  {mode === "parent" && (
    <button
      onClick={() => setShowSettings(true)}
      style={{
        position: "absolute", right: 16,
        background: "rgba(255,255,255,0.2)", border: "none",
        borderRadius: "50%", width: 38, height: 38,
        fontSize: 20, cursor: "pointer", display: "flex",
        alignItems: "center", justifyContent: "center"
      }}
    >⚙️</button>
  )}
</div>

{/* REFERRAL BANNER — parent only */}
{mode === "parent" && referralCode && (
  <div style={{
    margin: "10px 16px 0",
    background: "linear-gradient(135deg, #fff8f0, #fff3e0)",
    border: "1px solid #ffcc80",
    borderRadius: 10,
    padding: "7px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10
  }}>
    <div style={{ flex: 1, fontSize: 12, fontWeight: "600", color: "#e65100" }}>
      🏆 {referralCount >= 3 ? "🎉 Free month earned!" : `${referralCount}/3 referrals · Get 1 month free`}
    </div>
    <button
      onClick={() => {
        const link = `https://app.chore-ace.com?ref=${referralCode}`;
        const text = `I've been using Chore Ace to teach the kids a bit of work ethic — they do their chores and earn their own pocket money, no more nagging! 🏆 Try it free using my link and we both get a month free: ${link}`;
        if (navigator.share) {
          navigator.share({ title: "Chore Ace", text, url: link });
        } else {
          navigator.clipboard.writeText(link);
          setReferralCopied(true);
          setTimeout(() => setReferralCopied(false), 2000);
        }
      }}
      style={{
        background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)",
        color: "#fff", border: "none", borderRadius: 10,
        padding: "8px 12px", fontWeight: "700", fontSize: 12, cursor: "pointer",
        whiteSpace: "nowrap"
      }}
    >
      {referralCopied ? "✅ Copied!" : "Share 🔗"}
    </button>
  </div>
)}

{message && (
  <>
    {showTick && (
  <div style={{
    position: "fixed",
    top: "40%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    fontSize: 70,
    color: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)",
    textShadow: "0 0 20px rgba(76,175,80,0.6)",
    zIndex: 100,
    animation: "successPop 0.8s cubic-bezier(0.25, 1, 0.5, 1)"
  }}>
    ✅
  </div>
)}

    <div style={{
      background: "#e6fffa",
      padding: 10,
      borderRadius: 10,
      marginBottom: 10,
      textAlign: "center",
      fontWeight: "500"
    }}>
      {message}
    </div>
  </>
)}


{/* ✅ EDIT CHILD NAME MODAL */}
{editChildName && (
  <div style={styles.overlay}>
    <div style={styles.modal}>

      <div style={{ fontWeight: "600", marginBottom: 4 }}>Child name</div>
      <input
        value={children.find(c => c.id === activeChild)?.name || ""}
        onChange={(e) => {
          const newName = e.target.value;
          setChildren(children.map(c =>
            c.id === activeChild ? { ...c, name: newName } : c
          ));
        }}
        placeholder="Nickname or first name"
      />

      <div style={{ fontWeight: "600", marginTop: 12, marginBottom: 4 }}>
        Set PIN <span style={{ fontWeight: "400", fontSize: 13, opacity: 0.6 }}>(4 digits — leave blank to keep existing)</span>
      </div>
      <input
        type="password"
        inputMode="numeric"
        maxLength={4}
        placeholder="e.g. 1234"
        value={pinInput}
        onChange={e => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
      />

      <button
        onClick={async () => {
          const current = children.find(c => c.id === activeChild)?.name;
          if (!current?.trim()) return;

          const update = { name: current };
          if (pinInput.length === 4) {
            update.pinHash = await hashPin(pinInput);
          }

          await updateDoc(doc(db, "children", activeChild), update);
          setPinInput("");
          setEditChildName(false);
          setMessage("✅ Saved!");
          setTimeout(() => setMessage(""), 2000);
        }}
      >
        Save ✅
      </button>

      <button
        onClick={async () => {
          if (!window.confirm(`Delete ${children.find(c => c.id === activeChild)?.name}? This cannot be undone.`)) return;
          const choreSnap = await getDocs(query(collection(db, "chores"), where("childId", "==", activeChild)));
          for (const choreDoc of choreSnap.docs) await deleteDoc(doc(db, "chores", choreDoc.id));
          await deleteDoc(doc(db, "children", activeChild));
          setActiveChild(null);
          setPinInput("");
          setEditChildName(false);
        }}
        style={{ background: "none", border: "none", color: "#e53935", cursor: "pointer", marginTop: 4, fontWeight: "600" }}
      >
        Delete Child 🗑️
      </button>

      <button
        onClick={() => { setPinInput(""); setEditChildName(false); }}
        style={{ background: "none", border: "none", color: "#999", cursor: "pointer", marginTop: 4 }}
      >
        Cancel
      </button>

    </div>
  </div>
)}


{/* CREATE LEAGUE MODAL */}
{showCreateLeague && (
  <div style={styles.overlay}>
    <div style={styles.modal}>
      <div style={{ fontWeight: "700", fontSize: 18 }}>🏆 Create League</div>
      <div style={{ fontSize: 13, opacity: 0.6 }}>Give your league a name — you'll get a code to share with other families.</div>
      <input
        placeholder="e.g. Street Champions"
        value={leagueName}
        onChange={e => setLeagueName(e.target.value)}
      />
      <button style={{ ...styles.button, marginTop: 8 }} onClick={createLeague}>Create ✅</button>
      <button onClick={() => setShowCreateLeague(false)} style={{ background: "none", border: "none", color: "#999", cursor: "pointer", marginTop: 4 }}>Cancel</button>
    </div>
  </div>
)}

{/* JOIN LEAGUE MODAL */}
{showJoinLeague && (
  <div style={styles.overlay}>
    <div style={styles.modal}>
      <div style={{ fontWeight: "700", fontSize: 18 }}>🔗 Join a League</div>
      <div style={{ fontSize: 13, opacity: 0.6 }}>Enter the invite code from the league owner.</div>
      <input
        placeholder="6-digit code (e.g. AB12CD)"
        value={joinCode}
        onChange={e => setJoinCode(e.target.value.toUpperCase())}
        style={{ fontFamily: "monospace", letterSpacing: 2, fontWeight: "700" }}
      />
      {mode === "parent" && (
        <>
          <div style={{ fontWeight: "600", marginTop: 8, marginBottom: 4, fontSize: 14 }}>Which child is joining?</div>
          {children.map(c => (
            <button
              key={c.id}
              onClick={() => setJoinChildId(c.id)}
              style={{
                width: "100%", padding: "10px", borderRadius: 10, marginBottom: 6, border: "none",
                background: joinChildId === c.id ? "#ff6a00" : "#f3f4f6",
                color: joinChildId === c.id ? "#fff" : "#333",
                fontWeight: "600", cursor: "pointer"
              }}
            >
              👤 {c.name}
            </button>
          ))}
        </>
      )}
      {mode === "child" && (
        <div style={{ fontSize: 14, opacity: 0.7, marginTop: 8 }}>
          Joining as: <strong>{children.find(c => c.id === activeChild)?.name}</strong>
        </div>
      )}
      <button style={{ ...styles.button, marginTop: 8, width: "100%" }} onClick={requestJoinLeague} disabled={!joinCode || (mode === "parent" && !joinChildId)}>
        Send Request 🚀
      </button>
      <button onClick={() => setShowJoinLeague(false)} style={{ background: "none", border: "none", color: "#999", cursor: "pointer", marginTop: 4 }}>Cancel</button>
    </div>
  </div>
)}

{/* SUBSCRIPTION MODAL */}
{showSubscribeModal && (
  <div style={styles.overlay}>
    <div style={{ ...styles.modal, textAlign: "center", gap: 0 }}>

      <div style={{ fontSize: 48, marginBottom: 8 }}>⭐</div>

      <div style={{ fontWeight: "800", fontSize: 20, marginBottom: 6 }}>
        Upgrade to Family Plan
      </div>

      <div style={{ fontSize: 14, opacity: 0.6, marginBottom: 20 }}>
        Add unlimited children and unlock all features
      </div>

      <div style={{
        background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)",
        borderRadius: 14,
        padding: "16px",
        marginBottom: 20,
        color: "#fff"
      }}>
        <div style={{ fontSize: 32, fontWeight: "800" }}>£3.99</div>
        <div style={{ fontSize: 13, opacity: 0.85 }}>per month — all access</div>
      </div>

      <div style={{ textAlign: "left", fontSize: 14, marginBottom: 20, display: "flex", flexDirection: "column", gap: 8 }}>
        <div>✅ Unlimited children</div>
        <div>✅ Editable rewards & streaks</div>
        <div>✅ Family leaderboard & leagues</div>
        <div>✅ Photo proof uploads</div>
      </div>

      {mode === "parent" && (
        window.Capacitor?.isNativePlatform?.() ? (
          <div style={{ textAlign: "center", fontSize: 14, color: "#666", lineHeight: 1.6 }}>
            <div style={{ fontWeight: "700", color: "#e65100", marginBottom: 8 }}>Subscribe on our website</div>
            <div style={{ marginBottom: 12 }}>Visit <strong>app.chore-ace.com</strong> in your browser to subscribe. Your subscription will sync to the app automatically.</div>
            <a href="https://app.chore-ace.com" target="_blank" rel="noreferrer"
              style={{ display: "block", padding: "14px", background: "linear-gradient(135deg,#ff4000,#ff6a00,#ff9500)", color: "#fff", borderRadius: 14, fontWeight: "700", fontSize: 15, textDecoration: "none" }}>
              Go to app.chore-ace.com 🚀
            </a>
          </div>
        ) : (
        <button
          style={{ ...styles.button, width: "100%", textAlign: "center", marginTop: 0 }}
          onClick={handleSubscribe}
          disabled={checkoutLoading}
        >
          {checkoutLoading ? "Redirecting to payment…" : "Subscribe Now 🚀"}
        </button>
        )
      )}

      {mode === "child" && (
        upgradeRequested ? (
          <div style={{
            background: "#e8f5e9", borderRadius: 12, padding: "12px 16px",
            fontSize: 14, color: "#2e7d32", fontWeight: "600", textAlign: "center"
          }}>
            ✅ Request sent to your parent!
          </div>
        ) : (
          <button
            style={{ ...styles.button, width: "100%", textAlign: "center", marginTop: 0, background: "linear-gradient(135deg, #7b1fa2, #ab47bc)" }}
            onClick={handleUpgradeRequest}
          >
            📩 Ask Parent to Upgrade
          </button>
        )
      )}

      <button
        onClick={() => setShowSubscribeModal(false)}
        style={{ background: "none", border: "none", color: "#999", cursor: "pointer", marginTop: 12, fontSize: 14 }}
      >
        Maybe later
      </button>

    </div>
  </div>
)}

{/* UPGRADE REQUEST BANNER — parent only */}
{mode === "parent" && upgradeRequested && !isSubscribed && (
  <div style={{
    background: "linear-gradient(135deg, #7b1fa2, #ab47bc)",
    color: "#fff",
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexShrink: 0
  }}>
    <div style={{ fontSize: 14, fontWeight: "600" }}>
      📩 A child has requested a Family Plan upgrade!
    </div>
    <button
      onClick={() => setShowSubscribeModal(true)}
      style={{
        background: "#fff",
        color: "#7b1fa2",
        border: "none",
        borderRadius: 20,
        padding: "6px 14px",
        fontWeight: "700",
        fontSize: 13,
        cursor: "pointer",
        whiteSpace: "nowrap"
      }}
    >
      View
    </button>
  </div>
)}

{/* 🎉 CELEBRATION OVERLAY */}
{celebration && (() => {
  const celAv = avatars.find(a => a.id === celebration.avatar);
  const celSkin = SKIN_TONES.find(s => s.id === celebration.avatarSkin)?.hex;
  const stars = ["⭐","🌟","✨","💫","⭐","🌟","✨","💫","⭐","🌟"];
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9998, overflow: "hidden",
      background: "linear-gradient(160deg, #ff4000, #ff6a00 50%, #ff9500)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      animation: "fadeIn 0.3s ease"
    }}>

      {/* Floating stars background */}
      {stars.map((s, i) => (
        <div key={i} style={{
          position: "absolute",
          left: `${8 + i * 9}%`,
          bottom: "-10%",
          fontSize: `${16 + (i % 3) * 10}px`,
          animation: `starFloat ${2.5 + (i % 4) * 0.6}s ease-in ${i * 0.18}s infinite`,
          pointerEvents: "none",
          opacity: 0.7
        }}>{s}</div>
      ))}

      {/* Avatar */}
      <div style={{ animation: "avatarBounce 0.6s cubic-bezier(0.34,1.56,0.64,1) both" }}>
        {celAv
          ? <span dangerouslySetInnerHTML={{ __html: applySkin(celAv.svg, celSkin) }}
              style={{ width: 110, height: 110, borderRadius: "50%", overflow: "hidden", display: "block",
                boxShadow: "0 8px 32px rgba(0,0,0,0.25)", border: "4px solid rgba(255,255,255,0.6)" }} />
          : <div style={{ fontSize: 90 }}>🌟</div>
        }
      </div>

      {/* Name + message */}
      <div style={{ animation: "slideUp 0.5s ease 0.3s both", textAlign: "center", padding: "0 28px" }}>
        <div style={{ fontSize: 30, fontWeight: "900", color: "#fff", marginTop: 16, textShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
          Amazing{celebration.childName ? `, ${celebration.childName}` : ""}! 🎉
        </div>
        <div style={{ fontSize: 17, color: "rgba(255,255,255,0.9)", marginTop: 6 }}>
          {celebration.choreName}
        </div>
      </div>

      {/* Pay badge */}
      <div style={{ animation: "coinDrop 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.55s both",
        marginTop: 22, background: "#fff", borderRadius: 24, padding: "14px 36px",
        fontSize: 32, fontWeight: "900", color: "#ff6a00",
        boxShadow: "0 6px 24px rgba(0,0,0,0.18)" }}>
        +£{parseFloat(celebration.pay).toFixed(2)}
      </div>

      {/* Streak badge */}
      {celebration.streak >= 2 && (
        <div style={{ animation: "slideUp 0.5s ease 0.75s both",
          marginTop: 14, background: "rgba(255,255,255,0.2)", borderRadius: 20,
          padding: "7px 18px", color: "#fff", fontWeight: "700", fontSize: 15 }}>
          🔥 {celebration.streak} day streak!
        </div>
      )}

      <div style={{ animation: "slideUp 0.4s ease 0.85s both", color: "rgba(255,255,255,0.75)", marginTop: 14, fontSize: 13 }}>
        Waiting for parent approval ✅
      </div>

      {/* Buttons */}
      <button
        onClick={() => setCelebration(null)}
        style={{
          animation: "slideUp 0.4s ease 1s both",
          marginTop: 28, background: "rgba(255,255,255,0.25)", border: "2px solid rgba(255,255,255,0.5)",
          borderRadius: 24, padding: "12px 36px", color: "#fff",
          fontWeight: "800", fontSize: 16, cursor: "pointer",
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)"
        }}
      >
        Keep it up! 💪
      </button>
      <button
        onClick={async () => {
          await updateDoc(doc(db, "chores", celebration.choreId), { done: false, photo: null, photoPath: null, completedAt: null });
          await updateDoc(doc(db, "children", activeChild), { streak: Math.max((streak - 1), 0) });
          setCelebration(null);
        }}
        style={{
          animation: "slideUp 0.4s ease 1.1s both",
          marginTop: 10, background: "none", border: "none",
          color: "rgba(255,255,255,0.5)", fontSize: 13, cursor: "pointer"
        }}
      >
        ↩ Undo (marked by mistake)
      </button>
    </div>
  );
})()}

{/* 🖼 PHOTO LIGHTBOX */}
{photoLightbox && (
  <div
    onClick={() => setPhotoLightbox(null)}
    style={{
      position: "fixed", inset: 0, zIndex: 9997, background: "rgba(0,0,0,0.85)",
      display: "flex", alignItems: "center", justifyContent: "center"
    }}
  >
    <img src={photoLightbox} alt="proof" style={{ maxWidth: "90vw", maxHeight: "80vh", borderRadius: 12 }} />
    <div style={{ position: "absolute", top: 20, right: 20, color: "#fff", fontSize: 28, cursor: "pointer" }}>✕</div>
  </div>
)}

{/* 👨‍👩‍👧 SECOND PARENT CODE MODAL */}
{showSecondParentCode && (
  <div style={{ ...styles.overlay, zIndex: 20 }}>
    <div style={{ ...styles.modal, gap: 12, textAlign: "center" }}>
      <div style={{ fontSize: 40 }}>👨‍👩‍👧</div>
      <div style={{ fontWeight: "800", fontSize: 18 }}>Add Second Parent</div>
      <div style={{ fontSize: 13, opacity: 0.6 }}>Share this code with the other parent. They sign into Chore Ace with their Google account, go to Settings and tap "Join as Second Parent".</div>
      <div style={{ background: "#f3f4f6", borderRadius: 10, padding: "14px", fontFamily: "monospace", fontWeight: "800", fontSize: 28, letterSpacing: 6, color: "#1565c0" }}>
        {secondParentCode}
      </div>
      <button
        onClick={() => { navigator.clipboard.writeText(secondParentCode); setMessage("✅ Code copied!"); setTimeout(() => setMessage(""), 2000); }}
        style={{ ...styles.button, marginTop: 0, width: "100%", textAlign: "center" }}
      >📋 Copy Code</button>
      <a
        href={`https://wa.me/?text=${encodeURIComponent(`Hi! Here is the Chore Ace family code to join our household: ${secondParentCode}\n\nOpen Chore Ace, sign in with Google, go to Settings and tap "Join as Second Parent" and enter this code.`)}`}
        target="_blank" rel="noreferrer"
        style={{ display: "block", textAlign: "center", padding: "12px", background: "#25D366", color: "#fff", borderRadius: 12, fontWeight: "700", fontSize: 14, textDecoration: "none" }}
      >Share via WhatsApp</a>
      <button onClick={() => setShowSecondParentCode(false)} style={{ background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 14 }}>Done</button>
    </div>
  </div>
)}

{/* 👨‍👩‍👧 JOIN FAMILY MODAL */}
{showJoinFamily && (
  <div style={{ ...styles.overlay, zIndex: 20 }}>
    <div style={{ ...styles.modal, gap: 12 }}>
      <div style={{ fontWeight: "800", fontSize: 18 }}>👨‍👩‍👧 Join as Second Parent</div>
      <div style={{ fontSize: 13, opacity: 0.6 }}>Enter the code from the other parent to link your account to their household.</div>
      <input
        placeholder="Enter family code"
        value={joinFamilyCode}
        onChange={e => setJoinFamilyCode(e.target.value.toUpperCase())}
        style={{ width: "100%", boxSizing: "border-box", fontFamily: "monospace", fontWeight: "700", fontSize: 20, letterSpacing: 4, textAlign: "center" }}
      />
      {joinFamilyError && <div style={{ color: "#e53935", fontSize: 13 }}>{joinFamilyError}</div>}
      <button onClick={joinFamilyAsSecondParent} style={{ ...styles.button, marginTop: 0, width: "100%", textAlign: "center" }}>Join Family 👨‍👩‍👧</button>
      <button onClick={() => setShowJoinFamily(false)} style={{ background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 14 }}>Cancel</button>
    </div>
  </div>
)}

{/* 🏠 SPLIT HOUSEHOLD SETUP MODAL */}
{showSplitSetup && (
  <div style={{ ...styles.overlay, zIndex: 20 }}>
    <div style={{ ...styles.modal, gap: 12 }}>
      <div style={{ fontWeight: "800", fontSize: 18 }}>🏠 Split Household</div>
      <div style={{ fontSize: 13, opacity: 0.6 }}>Set up this child for two households. Each parent manages their own chores and pocket money separately.</div>

      <div>
        <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 4 }}>What do you call your household?</div>
        <input placeholder="e.g. Mum's, Dad's, Home..." value={splitHouseholdName} onChange={e => setSplitHouseholdName(e.target.value)} style={{ width: "100%", boxSizing: "border-box" }} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 4 }}>What is the other household called?</div>
        <input placeholder="e.g. Dad's, Mum's, Nan's..." value={splitOtherName} onChange={e => setSplitOtherName(e.target.value)} style={{ width: "100%", boxSizing: "border-box" }} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 4 }}>Set a PIN for your household</div>
        <input type="password" inputMode="numeric" maxLength={4} placeholder="• • • •" value={splitPin} onChange={e => setSplitPin(e.target.value.replace(/\D/g, "").slice(0, 4))} style={{ width: "100%", boxSizing: "border-box", fontSize: 20, letterSpacing: 8, textAlign: "center" }} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 4 }}>Confirm PIN</div>
        <input type="password" inputMode="numeric" maxLength={4} placeholder="• • • •" value={splitPin2} onChange={e => setSplitPin2(e.target.value.replace(/\D/g, "").slice(0, 4))} style={{ width: "100%", boxSizing: "border-box", fontSize: 20, letterSpacing: 8, textAlign: "center" }} />
      </div>
      {splitError && <div style={{ color: "#e53935", fontSize: 13 }}>{splitError}</div>}
      <button onClick={() => setupSplitHousehold(showSplitSetup)} style={{ ...styles.button, marginTop: 0, width: "100%", textAlign: "center" }}>Generate Split Code 🏠</button>
      <button onClick={() => setShowSplitSetup(null)} style={{ background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 14 }}>Cancel</button>
    </div>
  </div>
)}

{/* 🏠 SPLIT CODE SHARE MODAL */}
{showSplitCode && (
  <div style={{ ...styles.overlay, zIndex: 20 }}>
    <div style={{ ...styles.modal, gap: 12, textAlign: "center" }}>
      <div style={{ fontSize: 40 }}>🏠</div>
      <div style={{ fontWeight: "800", fontSize: 18 }}>Split Household Ready!</div>
      <div style={{ fontSize: 13, opacity: 0.6 }}>Share this code with the other parent so they can link the child to their app.</div>
      <div style={{ background: "#f3f4f6", borderRadius: 10, padding: "14px", fontFamily: "monospace", fontWeight: "800", fontSize: 28, letterSpacing: 6, color: "#ff6a00" }}>
        {splitCode}
      </div>
      <button
        onClick={() => { navigator.clipboard.writeText(splitCode); setMessage("✅ Code copied!"); setTimeout(() => setMessage(""), 2000); }}
        style={{ ...styles.button, marginTop: 0, width: "100%", textAlign: "center" }}
      >📋 Copy Code</button>
      <a
        href={`https://wa.me/?text=${encodeURIComponent(`Hi! Here is the Chore Ace split household code to link our child to your app: ${splitCode}\n\nOpen Chore Ace, go to Settings → Link Split Household Child, and enter this code.`)}`}
        target="_blank" rel="noreferrer"
        style={{ display: "block", textAlign: "center", padding: "12px", background: "#25D366", color: "#fff", borderRadius: 12, fontWeight: "700", fontSize: 14, textDecoration: "none" }}
      >Share via WhatsApp</a>
      <button onClick={() => setShowSplitCode(false)} style={{ background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 14 }}>Done</button>
    </div>
  </div>
)}

{/* 🏠 JOIN SPLIT HOUSEHOLD MODAL */}
{showJoinSplit && (
  <div style={{ ...styles.overlay, zIndex: 20 }}>
    <div style={{ ...styles.modal, gap: 12 }}>
      <div style={{ fontWeight: "800", fontSize: 18 }}>🏠 Link Split Household Child</div>
      <div style={{ fontSize: 13, opacity: 0.6 }}>Enter the code from the other parent to add the child to your app with their own PIN and separate balance.</div>
      <div>
        <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 4 }}>Split household code</div>
        <input placeholder="e.g. AB1C2D" value={joinSplitCode} onChange={e => setJoinSplitCode(e.target.value.toUpperCase())} style={{ width: "100%", boxSizing: "border-box", fontFamily: "monospace", fontWeight: "700", fontSize: 18, letterSpacing: 4, textAlign: "center" }} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 4 }}>Child's name (optional — leave blank to use existing name)</div>
        <input placeholder="e.g. Emma" value={joinSplitName} onChange={e => setJoinSplitName(e.target.value)} style={{ width: "100%", boxSizing: "border-box" }} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 4 }}>Set a PIN for your household</div>
        <input type="password" inputMode="numeric" maxLength={4} placeholder="• • • •" value={joinSplitPin} onChange={e => setJoinSplitPin(e.target.value.replace(/\D/g, "").slice(0, 4))} style={{ width: "100%", boxSizing: "border-box", fontSize: 20, letterSpacing: 8, textAlign: "center" }} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 4 }}>Confirm PIN</div>
        <input type="password" inputMode="numeric" maxLength={4} placeholder="• • • •" value={joinSplitPin2} onChange={e => setJoinSplitPin2(e.target.value.replace(/\D/g, "").slice(0, 4))} style={{ width: "100%", boxSizing: "border-box", fontSize: 20, letterSpacing: 8, textAlign: "center" }} />
      </div>
      {joinSplitError && <div style={{ color: "#e53935", fontSize: 13 }}>{joinSplitError}</div>}
      <button onClick={joinSplitHousehold} style={{ ...styles.button, marginTop: 0, width: "100%", textAlign: "center" }}>Link Child 🏠</button>
      <button onClick={() => setShowJoinSplit(false)} style={{ background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 14 }}>Cancel</button>
    </div>
  </div>
)}

{/* ⚙️ SETTINGS MODAL */}
{deletePrompt && createPortal(
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
    <div style={{ background: "#fff", borderRadius: 20, padding: 28, maxWidth: 340, width: "100%", textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🗑</div>
      <div style={{ fontWeight: "800", fontSize: 19, marginBottom: 8 }}>Delete "{deletePrompt.name}"?</div>
      <div style={{ fontSize: 14, color: "#666", marginBottom: 20 }}>This is a recurring chore. Do you want to remove just today, or the whole series?</div>
      <button
        onClick={() => { remove(deletePrompt.id, deletePrompt.occurrenceDate); setDeletePrompt(null); }}
        style={{ width: "100%", padding: "13px 0", borderRadius: 14, border: "1.5px solid #ff6a00", background: "#fff8f0", color: "#ff6a00", fontWeight: "700", fontSize: 15, cursor: "pointer", marginBottom: 10 }}
      >Skip this day only</button>
      <button
        onClick={() => { remove(deletePrompt.id); setDeletePrompt(null); }}
        style={{ width: "100%", padding: "13px 0", borderRadius: 14, border: "none", background: "#ff4444", color: "#fff", fontWeight: "700", fontSize: 15, cursor: "pointer", marginBottom: 10 }}
      >Delete entire series ❌</button>
      <button
        onClick={() => setDeletePrompt(null)}
        style={{ width: "100%", padding: "10px 0", borderRadius: 14, border: "1px solid #ddd", background: "#f9f9f9", color: "#666", fontWeight: "600", fontSize: 14, cursor: "pointer" }}
      >Cancel</button>
    </div>
  </div>,
  document.body
)}

{showReviewPrompt && createPortal(
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
    <div style={{ background: "#fff", borderRadius: 20, padding: 28, maxWidth: 340, width: "100%", textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>⭐</div>
      <div style={{ fontWeight: "800", fontSize: 20, marginBottom: 8 }}>Enjoying Chore Ace?</div>
      <div style={{ fontSize: 14, color: "#666", marginBottom: 20 }}>You're on a roll! A quick review on the App Store helps other families find us. It takes 30 seconds and means the world to us! 🙏</div>
      <button
        onClick={() => { window.open("https://apps.apple.com/app/chore-ace/id6786621536", "_blank"); setShowReviewPrompt(false); }}
        style={{ width: "100%", padding: "14px 0", borderRadius: 14, border: "none", background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)", color: "#fff", fontWeight: "700", fontSize: 16, cursor: "pointer", marginBottom: 10 }}
      >
        ⭐ Leave a Review
      </button>
      <button
        onClick={() => setShowReviewPrompt(false)}
        style={{ width: "100%", padding: "10px 0", borderRadius: 14, border: "none", background: "none", color: "#999", fontSize: 14, cursor: "pointer" }}
      >
        Maybe later
      </button>
    </div>
  </div>,
  document.body
)}

{showReferralPrompt && createPortal(
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
    <div style={{ background: "#fff", borderRadius: 20, padding: 28, maxWidth: 340, width: "100%", textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🎁</div>
      <div style={{ fontWeight: "800", fontSize: 20, marginBottom: 8 }}>Know another family who'd love this?</div>
      <div style={{ fontSize: 14, color: "#666", marginBottom: 20 }}>Chore Ace is helping your kids build real work ethic — earning their own pocket money instead of just being handed it. Refer 3 friends and get a free month!</div>
      <button
        onClick={() => {
          const link = `https://app.chore-ace.com?ref=${referralCode}`;
          const text = `I've been using Chore Ace to teach the kids a bit of work ethic — they do their chores and earn their own pocket money, no more nagging! 🏆 Try it free using my link and we both get a month free: ${link}`;
          if (navigator.share) {
            navigator.share({ title: "Chore Ace", text, url: link });
          } else {
            navigator.clipboard.writeText(link);
          }
          setShowReferralPrompt(false);
        }}
        style={{ width: "100%", padding: "14px 0", borderRadius: 14, border: "none", background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)", color: "#fff", fontWeight: "700", fontSize: 16, cursor: "pointer", marginBottom: 10 }}
      >
        🔗 Share & Earn a Free Month
      </button>
      <button
        onClick={() => setShowReferralPrompt(false)}
        style={{ width: "100%", padding: "10px 0", borderRadius: 14, border: "none", background: "none", color: "#999", fontSize: 14, cursor: "pointer" }}
      >
        Maybe later
      </button>
    </div>
  </div>,
  document.body
)}

{showSettings && (
  <div style={styles.overlay}>
    <div style={{ ...styles.modal, gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontWeight: "800", fontSize: 18 }}>⚙️ Settings</div>
        <button onClick={() => setShowSettings(false)} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", lineHeight: 1, color: "#999", padding: "0 2px" }}>✕</button>
      </div>

      {/* Family name */}
      <div>
        <div style={{ fontSize: 13, fontWeight: "600", opacity: 0.6, marginBottom: 6 }}>Family name</div>
        {editingFamilyName ? (
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={familyName}
              onChange={e => setFamilyName(e.target.value)}
              style={{ flex: 1 }}
            />
            <button
              onClick={async () => {
                await setDoc(doc(db, "meta", `family_${familyId}`), { familyName }, { merge: true });
                setEditingFamilyName(false);
              }}
              style={{ ...styles.button, marginTop: 0, padding: "8px 14px" }}
            >Save</button>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: "600" }}>{familyName || "Not set"}</span>
            <button onClick={() => setEditingFamilyName(true)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18 }}>✏️</button>
          </div>
        )}
      </div>

      <div style={{ height: 1, background: "#f0f0f0" }} />

      {/* Family code */}
      <div>
        <div style={{ fontSize: 13, fontWeight: "600", opacity: 0.6, marginBottom: 6 }}>Family code</div>
        <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 8 }}>
          Share this with your children so they can log in.
        </div>
        <div style={{
          background: "#f3f4f6", borderRadius: 10, padding: "10px 14px",
          fontFamily: "monospace", fontWeight: "700", fontSize: 15, letterSpacing: 1,
          wordBreak: "break-all"
        }}>
          {familyId}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button
            onClick={() => {
              navigator.clipboard.writeText(familyId);
              setCodeCopied(true);
              setTimeout(() => setCodeCopied(false), 2000);
            }}
            style={{ ...styles.button, flex: 1, textAlign: "center", marginTop: 0 }}
          >
            {codeCopied ? "✅ Copied!" : "📋 Copy Code"}
          </button>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`Join our family on Chore Ace! 🏆\n\nOpen the app: https://app.chore-ace.com\nFamily code: ${familyId}`)}`}
            target="_blank"
            rel="noreferrer"
            style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              background: "#25D366", color: "#fff", borderRadius: 12, padding: "10px",
              fontWeight: "700", fontSize: 14, textDecoration: "none"
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp
          </a>
        </div>
      </div>

      <div style={{ height: 1, background: "#f0f0f0" }} />

      {/* Children */}
      <div>
        <div style={{ fontSize: 13, fontWeight: "600", opacity: 0.6, marginBottom: 8 }}>Children</div>
        {children.map(c => {
          const av = avatars.find(a => a.id === c.avatar);
          return (
          <div key={c.id} style={{ padding: "10px 0", borderBottom: "1px solid #f0f0f0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  onClick={() => { setShowSettings(false); setShowAvatarPicker(c.id); }}
                  style={{ width: 32, height: 32, borderRadius: "50%", overflow: "hidden", cursor: "pointer", border: "2px solid #eee", flexShrink: 0 }}
                >
                  {av
                    ? <span dangerouslySetInnerHTML={{ __html: applySkin(av.svg, SKIN_TONES.find(s => s.id === c.avatarSkin)?.hex) }} style={{ width: 32, height: 32, display: "block" }} />
                    : <div style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f5f5", fontSize: 16 }}>👤</div>
                  }
                </div>
                <span style={{ fontWeight: "600" }}>{c.name}</span>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                <button
                  onClick={() => { setResetPinChild(c.id); setResetPinVal(""); setResetPinVal2(""); setResetPinError(""); }}
                  style={{ background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 13, fontWeight: "600" }}
                >🔑 Reset PIN</button>
                <button
                  onClick={() => { setShowSettings(false); setShowSplitSetup(c.id); setSplitHouseholdName(""); setSplitOtherName(""); setSplitPin(""); setSplitPin2(""); setSplitError(""); }}
                  style={{ background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 13, fontWeight: "600" }}
                >🏠 Split Household</button>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Delete ${c.name}? This cannot be undone.`)) return;
                    const choreSnap = await getDocs(query(collection(db, "chores"), where("childId", "==", c.id)));
                    for (const choreDoc of choreSnap.docs) await deleteDoc(doc(db, "chores", choreDoc.id));
                    await deleteDoc(doc(db, "children", c.id));
                  }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#ff4444", fontSize: 18 }}
                >🗑</button>
              </div>
            </div>

            {resetPinChild === c.id && (
              <div style={{ marginTop: 10, background: "#f9f9f9", borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 8 }}>Set new PIN for {c.name}</div>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="New PIN"
                  value={resetPinVal}
                  onChange={e => setResetPinVal(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  style={{ width: "100%", boxSizing: "border-box", fontSize: 20, letterSpacing: 8, textAlign: "center", marginBottom: 8 }}
                />
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="Confirm PIN"
                  value={resetPinVal2}
                  onChange={e => setResetPinVal2(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  style={{ width: "100%", boxSizing: "border-box", fontSize: 20, letterSpacing: 8, textAlign: "center", marginBottom: 6 }}
                />
                {resetPinError && <div style={{ color: "#e53935", fontSize: 13, marginBottom: 6 }}>{resetPinError}</div>}
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={async () => {
                      if (resetPinVal.length !== 4) { setResetPinError("PIN must be 4 digits"); return; }
                      if (resetPinVal !== resetPinVal2) { setResetPinError("PINs don't match"); return; }
                      const hash = await hashPin(resetPinVal);
                      await updateDoc(doc(db, "children", c.id), { pinHash: hash });
                      setResetPinChild(null);
                    }}
                    style={{ ...styles.button, flex: 1, marginTop: 0, padding: "8px" }}
                  >Save ✅</button>
                  <button
                    onClick={() => setResetPinChild(null)}
                    style={{ flex: 1, padding: "8px", borderRadius: 10, border: "1px solid #ddd", background: "#fff", cursor: "pointer" }}
                  >Cancel</button>
                </div>
              </div>
            )}
          </div>
          );
        })}
        <button
          onClick={() => { setShowSettings(false); addChild(); }}
          style={{ ...styles.button, marginTop: 10, width: "100%", textAlign: "center", background: "#4CAF50" }}
        >
          ➕ Add Child
        </button>
        <button
          onClick={() => { setShowSettings(false); setShowJoinSplit(true); setJoinSplitCode(""); setJoinSplitPin(""); setJoinSplitPin2(""); setJoinSplitName(""); setJoinSplitError(""); }}
          style={{ ...styles.button, marginTop: 8, width: "100%", textAlign: "center", background: "#e8f5e9", color: "#2e7d32" }}
        >
          🏠 Link Split Household Child
        </button>

        <button
          onClick={generateSecondParentCode}
          style={{ ...styles.button, marginTop: 8, width: "100%", textAlign: "center", background: "#e3f2fd", color: "#1565c0" }}
        >
          👨‍👩‍👧 Add Second Parent
        </button>
        <button
          onClick={() => { setShowSettings(false); setShowJoinFamily(true); setJoinFamilyCode(""); setJoinFamilyError(""); }}
          style={{ ...styles.button, marginTop: 8, width: "100%", textAlign: "center", background: "#e3f2fd", color: "#1565c0" }}
        >
          👨‍👩‍👧 Join as Second Parent
        </button>
      </div>

      <div style={{ height: 1, background: "#f0f0f0" }} />

      {/* Referral */}
      <div>
        <div style={{ fontSize: 13, fontWeight: "600", opacity: 0.6, marginBottom: 6 }}>🎁 Refer & earn</div>
        <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 8 }}>
          Refer 3 friends who subscribe and get 1 month free. {referralCount}/3 completed.
        </div>
        <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
          {[0,1,2].map(i => (
            <div key={i} style={{
              flex: 1, height: 8, borderRadius: 4,
              background: i < referralCount ? "#ff6a00" : "#f0f0f0"
            }}/>
          ))}
        </div>
        <div style={{
          background: "#f3f4f6", borderRadius: 10, padding: "10px 14px",
          fontFamily: "monospace", fontWeight: "700", fontSize: 13,
          wordBreak: "break-all", marginBottom: 10
        }}>
          app.chore-ace.com?ref={referralCode}
        </div>
        <button
          onClick={() => {
            const link = `https://app.chore-ace.com?ref=${referralCode}`;
            const text = `I've been using Chore Ace to teach the kids a bit of work ethic — they do their chores and earn their own pocket money, no more nagging! 🏆 Try it free using my link and we both get a month free: ${link}`;
            if (navigator.share) {
              navigator.share({ title: "Chore Ace", text, url: link });
            } else {
              navigator.clipboard.writeText(link);
              setReferralCopied(true);
              setTimeout(() => setReferralCopied(false), 2000);
            }
          }}
          style={{ ...styles.button, width: "100%", textAlign: "center", marginTop: 0 }}
        >
          {referralCopied ? "✅ Link Copied!" : "🔗 Share Referral Link"}
        </button>
      </div>

      <div style={{ height: 1, background: "#f0f0f0" }} />

      {/* Share the app */}
      <div>
        <div style={{ fontSize: 13, fontWeight: "600", opacity: 0.6, marginBottom: 8 }}>📲 Share Chore Ace</div>
        <button
          onClick={() => {
            const link = "https://app.chore-ace.com";
            const text = `Hey! I've been using Chore Ace with my kids to manage chores and pocket money — it's brilliant! 🏆 Check it out: ${link}`;
            if (navigator.share) {
              navigator.share({ title: "Chore Ace", text, url: link });
            } else {
              navigator.clipboard.writeText(link);
              setMessage("✅ Link copied!");
              setTimeout(() => setMessage(""), 2000);
            }
          }}
          style={{ ...styles.button, width: "100%", textAlign: "center", marginTop: 0, background: "#25D366", color: "#fff" }}
        >
          📲 Share with Friends & Family
        </button>
      </div>

      <div style={{ height: 1, background: "#f0f0f0" }} />

      {/* Rate us */}
      <button
        onClick={() => window.open("https://apps.apple.com/app/chore-ace/id6786621536", "_blank")}
        style={{ ...styles.button, width: "100%", textAlign: "center", background: "#ff6a00", marginTop: 0 }}
      >
        ⭐ Rate Chore Ace
      </button>

      <div style={{ height: 1, background: "#f0f0f0" }} />

      {/* Contact & Social */}
      <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: "600", opacity: 0.6, marginBottom: 10 }}>Get in touch</div>
        <a href="mailto:chris@chore-ace.com?subject=Chore Ace Feedback"
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 12, background: "#fff8f0", border: "1.5px solid #ffd5b0", textDecoration: "none", color: "#e65100", fontWeight: "700", fontSize: 14, marginBottom: 10 }}>
          ✉️ Contact us / Report a bug
        </a>
        <div style={{ fontSize: 13, fontWeight: "600", opacity: 0.6, marginBottom: 8 }}>Follow us</div>
        <div style={{ display: "flex", gap: 8 }}>
          {[
            { label: "Facebook", icon: "📘", url: "https://facebook.com/profile.php?id=61590720218549" },
            { label: "Instagram", icon: "📸", url: "https://instagram.com/choreaceuk" },
            { label: "X", icon: "🐦", url: "https://x.com/ChoreAceUK" },
            { label: "TikTok", icon: "🎵", url: "https://tiktok.com/@choreaceuk" },
          ].map(s => (
            <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer"
              style={{ flex: 1, textAlign: "center", padding: "8px 4px", borderRadius: 10, background: "#f5f5f5", border: "1px solid #eee", textDecoration: "none", fontSize: 11, fontWeight: "700", color: "#333" }}>
              <div style={{ fontSize: 20 }}>{s.icon}</div>
              {s.label}
            </a>
          ))}
        </div>
      </div>

      {/* Sign out */}
      <button
        onClick={() => { setShowSettings(false); handleSignOut(); }}
        style={{ ...styles.button, width: "100%", textAlign: "center", background: "#ff4444", marginTop: 0 }}
      >
        👋 Sign Out
      </button>

      <button
        onClick={() => setShowSettings(false)}
        style={{ background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 14 }}
      >
        Close
      </button>
    </div>
  </div>
)}

{/* ADD CHILD MODAL */}
{showAddChild && (
  <div style={styles.overlay}>
    <div style={{ ...styles.modal, gap: 12 }}>
      <div style={{ fontWeight: "800", fontSize: 18 }}>➕ Add Child</div>
      <div>
        <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 6, opacity: 0.7 }}>Child's name</div>
        <input
          placeholder="e.g. Emma"
          value={addChildName}
          onChange={e => setAddChildName(e.target.value)}
          style={{ width: "100%", boxSizing: "border-box", fontSize: 16, padding: "12px 14px", borderRadius: 12, border: "1.5px solid #ddd" }}
        />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 6, opacity: 0.7 }}>Choose a 4-digit PIN</div>
        <input
          type="password"
          inputMode="numeric"
          maxLength={4}
          placeholder="• • • •"
          value={addChildPin}
          onChange={e => setAddChildPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
          style={{ width: "100%", boxSizing: "border-box", fontSize: 20, letterSpacing: 8, padding: "12px 14px", borderRadius: 12, border: "1.5px solid #ddd", textAlign: "center" }}
        />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 6, opacity: 0.7 }}>Confirm PIN</div>
        <input
          type="password"
          inputMode="numeric"
          maxLength={4}
          placeholder="• • • •"
          value={addChildPin2}
          onChange={e => setAddChildPin2(e.target.value.replace(/\D/g, "").slice(0, 4))}
          style={{ width: "100%", boxSizing: "border-box", fontSize: 20, letterSpacing: 8, padding: "12px 14px", borderRadius: 12, border: "1.5px solid #ddd", textAlign: "center" }}
        />
      </div>
      {addChildError && (
        <div style={{ background: "#ffeaea", border: "1px solid #ffcccc", borderRadius: 10, padding: "10px 14px", color: "#c62828", fontSize: 14, fontWeight: "600", textAlign: "center" }}>
          ⚠️ {addChildError}
        </div>
      )}
      <button
        onClick={confirmAddChild}
        disabled={!addChildName.trim() || addChildPin.length !== 4 || addChildPin2.length !== 4}
        style={{
          ...styles.button, width: "100%", textAlign: "center", marginTop: 4,
          background: (!addChildName.trim() || addChildPin.length !== 4 || addChildPin2.length !== 4)
            ? "#ccc" : "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)",
          cursor: (!addChildName.trim() || addChildPin.length !== 4 || addChildPin2.length !== 4) ? "not-allowed" : "pointer"
        }}
      >Save Child ✅</button>
      <button
        onClick={() => setShowAddChild(false)}
        style={{ background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 14 }}
      >Cancel</button>
    </div>
  </div>
)}

{/* SHARE CHILD LOGIN MODAL */}
{showShareChild && (() => {
  const appUrl = "https://app.chore-ace.com";
  const msg = `Hi ${showShareChild.name}! 🏆 You've been added to Chore Ace!\n\nOpen the app here: ${appUrl}\n\nTo log in:\n1. Tap "Child Login"\n2. Enter family code: ${familyId}\n3. Select your name\n4. Enter your PIN: ${showShareChild.pin}\n\nLet's go! 🚀`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(msg)}`;
  const smsUrl = `sms:?body=${encodeURIComponent(msg)}`;
  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.modal, gap: 14, textAlign: "center" }}>
        <div style={{ fontSize: 48 }}>🎉</div>
        <div style={{ fontWeight: "800", fontSize: 20 }}>{showShareChild.name} is all set!</div>
        <div style={{ fontSize: 14, opacity: 0.6 }}>Send their login details so they can get started</div>

        <div style={{ background: "#f9f9f9", borderRadius: 12, padding: 14, textAlign: "left", fontSize: 13, lineHeight: 1.6 }}>
          <div><strong>App:</strong> {appUrl}</div>
          <div><strong>Family code:</strong> <span style={{ fontFamily: "monospace", fontWeight: "700", letterSpacing: 1 }}>{familyId}</span></div>
          <div><strong>Name:</strong> {showShareChild.name}</div>
          <div><strong>PIN:</strong> {showShareChild.pin}</div>
        </div>

        <a
          href={whatsappUrl}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            background: "#25D366", color: "#fff", borderRadius: 14, padding: "14px",
            fontWeight: "700", fontSize: 16, textDecoration: "none"
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          Send via WhatsApp
        </a>

        <a
          href={smsUrl}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            background: "#4CAF50", color: "#fff", borderRadius: 14, padding: "14px",
            fontWeight: "700", fontSize: 16, textDecoration: "none"
          }}
        >
          💬 Send via iMessage / SMS
        </a>

        <button
          onClick={() => {
            navigator.clipboard.writeText(msg);
            setMessage("✅ Copied to clipboard!");
            setTimeout(() => setMessage(""), 2000);
          }}
          style={{ ...styles.button, width: "100%", textAlign: "center", marginTop: 0 }}
        >
          📋 Copy message
        </button>

        <button
          onClick={() => setShowShareChild(null)}
          style={{ background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 14 }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
})()}

{/* AVATAR PICKER MODAL */}
{showAvatarPicker && (() => {
  const pickerChild = children.find(c => c.id === showAvatarPicker);
  const currentSkin = SKIN_TONES.find(s => s.id === pickerChild?.avatarSkin) || SKIN_TONES[1];
  return (
  <div style={styles.overlay}>
    <div style={{ ...styles.modal, maxHeight: "80vh", overflowY: "auto" }}>
      <div style={{ fontWeight: "800", fontSize: 18, marginBottom: 4 }}>🎨 Pick Your Avatar</div>
      <div style={{ fontSize: 13, opacity: 0.6, marginBottom: 12 }}>Choose a character that's you!</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        {avatars.filter(av => {
          if (!av.testerOnly) return true;
          return TESTER_FAMILY_IDS.includes(familyId);
        }).map(av => {
          const selected = pickerChild?.avatar === av.id;
          return (
            <div
              key={av.id}
              onClick={async () => {
                await updateDoc(doc(db, "children", showAvatarPicker), { avatar: av.id });
              }}
              style={{
                cursor: "pointer", borderRadius: 14, padding: 6, textAlign: "center",
                border: selected ? "3px solid #FF6A00" : "2px solid #eee",
                background: selected ? "#fff8e1" : "#fafafa",
                transition: "all 0.15s ease"
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: applySkin(av.svg, currentSkin.hex) }} style={{ width: 64, height: 64, margin: "0 auto" }} />
              <div style={{ fontSize: 11, fontWeight: "600", marginTop: 4, opacity: 0.7 }}>{av.label}</div>
            </div>
          );
        })}
      </div>

      {/* Skin tone picker */}
      <div style={{ marginTop: 16, borderTop: "1px solid #f0f0f0", paddingTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: "700", marginBottom: 10 }}>Skin tone</div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          {SKIN_TONES.map(tone => (
            <div
              key={tone.id}
              onClick={async () => {
                await updateDoc(doc(db, "children", showAvatarPicker), { avatarSkin: tone.id });
              }}
              title={tone.label}
              style={{
                width: 36, height: 36, borderRadius: "50%", background: tone.hex,
                cursor: "pointer",
                border: currentSkin.id === tone.id ? "3px solid #FF6A00" : "3px solid transparent",
                boxShadow: currentSkin.id === tone.id ? "0 0 0 2px #fff, 0 0 0 4px #FF6A00" : "0 1px 3px rgba(0,0,0,0.2)",
                transition: "all 0.15s ease"
              }}
            />
          ))}
        </div>
      </div>

      <button
        onClick={() => setShowAvatarPicker(null)}
        style={{ background: "none", border: "none", color: "#999", cursor: "pointer", marginTop: 14, fontSize: 14 }}
      >Done</button>
    </div>
  </div>
  );
})()}

{/* ✅ SLIDER START */}
<div
  style={{
    width: "100vw",              // ✅ wrapper locked to screen width
    overflow: "hidden",
    position: "relative",
    flex: 1,
    minHeight: 0,
    touchAction: "auto"
  }}
  {...(tab !== "home" ? tabHandlers : {})}
>

  <div
    style={{
      display: "flex",
      height: "100%",
      transform: `translateX(-${tabs.indexOf(tab) * 100}vw)`,   // ✅ FIXED
      transition: "transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)"
    }}
  >

{/* ✅ HOME TAB */}
<div
  style={{
    flex: "0 0 100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    overflowY: "auto",
    paddingBottom: 80,
    boxSizing: "border-box"
  }}
>
{/* MODE */}
<div style={styles.row}>

  {/* Children — parent can switch between them, child only sees their own name */}
  {mode === "parent" ? children.map(child => {
    const av = avatars.find(a => a.id === child.avatar);
    return (
      <button
        key={child.id}
        onClick={() => setActiveChild(child.id)}
        style={{
          padding: "4px 12px 4px 4px", borderRadius: 20, border: "none",
          background: child.id === activeChild ? "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)" : "#eee",
          color: child.id === activeChild ? "#fff" : "#333",
          fontWeight: "600", cursor: "pointer", transition: "all 0.2s ease",
          display: "flex", alignItems: "center", gap: 6
        }}
      >
        {av
          ? <span dangerouslySetInnerHTML={{ __html: applySkin(av.svg, SKIN_TONES.find(s => s.id === child.avatarSkin)?.hex) }} style={{ width: 28, height: 28, borderRadius: "50%", overflow: "hidden", flexShrink: 0, display: "block" }} />
          : <span style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>👤</span>
        }
        {child.name}
      </button>
    );
  }) : (() => {
    const child = children.find(c => c.id === activeChild);
    const av = avatars.find(a => a.id === child?.avatar);
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          onClick={() => setShowAvatarPicker(activeChild)}
          style={{
            width: 36, height: 36, borderRadius: "50%", overflow: "hidden",
            cursor: "pointer", border: "2px solid #FF6A00", flexShrink: 0,
            background: "#fff"
          }}
          title="Change avatar"
        >
          {av
            ? <span dangerouslySetInnerHTML={{ __html: applySkin(av.svg, SKIN_TONES.find(s => s.id === child?.avatarSkin)?.hex) }} style={{ width: 36, height: 36, display: "block" }} />
            : <div style={{ width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, background: "#eee" }}>👤</div>
          }
        </div>
        <div style={{
          padding: "6px 12px", borderRadius: 20,
          background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)",
          color: "#fff", fontWeight: "600", fontSize: 14
        }}>
          {child?.name}
        </div>
        {!av && (
          <div
            onClick={() => setShowAvatarPicker(activeChild)}
            style={{ fontSize: 12, color: "#FF6A00", fontWeight: "600", cursor: "pointer" }}
          >Pick avatar ✨</div>
        )}
      </div>
    );
  })()}

  {/* Add child — parent only */}
  {mode === "parent" && (
    <button
      onClick={addChild}
      style={{
        padding: "6px 12px",
        borderRadius: 20,
        border: "none",
        background: "#ddd",
        fontWeight: "bold",
        cursor: "pointer"
      }}
    >
      +Child
    </button>
  )}

  {/* Edit active child — parent only */}
  {mode === "parent" && (
    <button
      onClick={() => setEditChildName(true)}
      style={{
        padding: "6px 12px",
        borderRadius: 20,
        border: "none",
        background: "#ddd",
        cursor: "pointer"
      }}
    >
      ✏️
    </button>
  )}

  {/* Role indicator + sign out */}
  {(mode === "child" || DEV_MODE) && (
    <button
      onClick={handleSignOut}
      style={{
        padding: "6px 12px", borderRadius: 20, border: "none",
        background: "#eee", color: "#333", cursor: "pointer", marginLeft: "auto",
        fontWeight: "600", fontSize: 13
      }}
    >
      {mode === "parent" ? "👧 Child view" : "🔒 Sign Out"}
    </button>
  )}

</div> 

<div style={styles.cardColumn}>

  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
    <div style={{ fontWeight: "600", fontSize: 14 }}>📋 Daily Progress</div>
    <div style={{ fontSize: 13, fontWeight: "700", color: progress === 100 ? "#4CAF50" : "#ff6a00" }}>{done}/{total} chores</div>
  </div>
  <div style={{ width: "100%", height: 10, background: "#eee", borderRadius: 6, overflow: "hidden" }}>
    <div style={{ ...styles.bar, width: `${progress}%`, transition: "width 0.4s ease" }} />
  </div>

  <div style={{ height: 1, background: "#f0f0f0", margin: "10px 0" }} />

  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
    <div style={{ fontWeight: "600", fontSize: 14 }}>📅 Weekly Progress</div>
    <div style={{ fontSize: 13, fontWeight: "700", color: weeklyProgress === 100 ? "#4CAF50" : "#ff6a00" }}>{weeklyDone}/{weeklyTotal} chores</div>
  </div>
  <div style={{ width: "100%", height: 10, background: "#eee", borderRadius: 6, overflow: "hidden" }}>
    <div style={{ ...styles.bar, width: `${weeklyProgress}%`, transition: "width 0.4s ease" }} />
  </div>
  <div style={{ fontSize: 11, opacity: 0.5, marginTop: 4 }}>
    🔄 Resets every Sunday · Week started {weekStart.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
  </div>

</div>

{mode === "parent" && chores.length > 0 && (
  <div style={{ display: "flex", gap: 10, marginBottom: 4 }}>
    {[
      {
        label: "Done this week",
        value: chores.filter(c => !c.suggested && c.done && thisWeekDateStrings.has(c.completedDate)).length,
        icon: "✅",
        color: "#e8f5e9",
        border: "#4caf50",
        textColor: "#2e7d32"
      },
      {
        label: "Pending approval",
        value: chores.filter(c => !c.suggested && c.done && !c.approved).length,
        icon: "⏳",
        color: "#fff8e1",
        border: "#f59e0b",
        textColor: "#b45309"
      },
      {
        label: "Paid out",
        value: `£${chores.filter(c => !c.suggested && c.approved && thisWeekDateStrings.has(c.approvedDate)).reduce((s, c) => s + (parseFloat(c.pay) || 0), 0).toFixed(2)}`,
        icon: "💰",
        color: "#fff3e0",
        border: "#ff6a00",
        textColor: "#e65100"
      },
    ].map(stat => (
      <div key={stat.label} style={{ flex: 1, background: stat.color, border: `1.5px solid ${stat.border}`, borderRadius: 12, padding: "10px 8px", textAlign: "center" }}>
        <div style={{ fontSize: 22 }}>{stat.icon}</div>
        <div style={{ fontWeight: "800", fontSize: 20, color: stat.textColor }}>{stat.value}</div>
        <div style={{ fontSize: 11, color: stat.textColor, opacity: 0.8, marginTop: 2 }}>{stat.label}</div>
      </div>
    ))}
  </div>
)}

{/* 🎯 TARGET REWARD CARD */}
{nextReward && (
  <div style={{
    ...styles.cardColumn,
    padding: 16,
    background: "#f8fff8",
    border: "2px solid #c8e6c9",
    display: "flex",
   flexDirection: "column",
    gap: 8,
    alignItems: "flex-start"   // ✅ KEY FIX
  }}>

    <div style={{ fontWeight: "600" }}>
      🎯 Current Target
    </div>

    <div style={{ fontSize: 18, fontWeight: "700" }}>
      {nextReward.name}
    </div>

    <div style={{ fontSize: 14, color: "#666" }}>
      🔥 {Math.max(nextReward.streak - streak, 0)} day{(nextReward.streak - streak !== 1 ? "s" : "")} to go
    </div>

    <div style={{
     width: "100%",
  background: "#eee",
  height: 10,
  borderRadius: 8,
  overflow: "hidden",
}}>
      <div style={{
        height: "100%",
        width: `${Math.min((streak / nextReward.streak) * 100, 100)}%`,
        background: "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)"
      }} />
    </div>

    {mode === "child" && (
      <button
        onClick={useStreakFreeze}
        style={{
          marginTop: 4,
          width: "100%",
          padding: "8px 12px",
          borderRadius: 10,
          border: "none",
          background: freezeAvailable ? "linear-gradient(135deg, #00bcd4, #0097a7)" : "#e0e0e0",
          color: freezeAvailable ? "#fff" : "#999",
          fontWeight: "700",
          fontSize: 13,
          cursor: freezeAvailable ? "pointer" : "default"
        }}
      >
        {freezeAvailable ? "🧊 Use Streak Freeze" : "🧊 Freeze used this month"}
      </button>
    )}

  </div>
)}


<div style={styles.cardColumn}>

  {mode === "parent" && children.length > 1 && (
    <div style={{ background: "#fff3e0", border: "1.5px solid #ff6a00", borderRadius: 10, padding: "8px 12px", fontSize: 14, fontWeight: "600", color: "#e65100", marginBottom: 4 }}>
      ➕ Adding chore for: {currentChildData.name || "..."}
    </div>
  )}

  {mode === "parent" && (
    <div style={{ marginBottom: 6 }}>
      <button
        onClick={() => setShowQuickAdd(v => !v)}
        style={{ fontSize: 13, fontWeight: "600", color: "#ff6a00", background: "#fff8f0", border: "1.5px solid #ff6a00", borderRadius: 20, padding: "5px 14px", cursor: "pointer", marginBottom: showQuickAdd ? 8 : 0 }}
      >
        ⚡ Quick add {showQuickAdd ? "▲" : "▼"}
      </button>
      {showQuickAdd && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {[
            { name: "Make bed 🛏️", pay: 0.50 },
            { name: "Tidy bedroom 🧹", pay: 1 },
            { name: "Wash dishes 🍽️", pay: 1 },
            { name: "Hoover living room 🧹", pay: 1.50 },
            { name: "Take out bins 🗑️", pay: 1 },
            { name: "Load dishwasher 🍽️", pay: 0.50 },
            { name: "Unload dishwasher 🍽️", pay: 0.50 },
            { name: "Wipe down kitchen 🧽", pay: 1 },
            { name: "Feed pet 🐾", pay: 0.50 },
            { name: "Lay the table 🍴", pay: 0.50 },
            { name: "Bring washing down 🧺", pay: 0.50 },
            { name: "Put washing away 👕", pay: 1 },
            { name: "Clean bathroom 🚿", pay: 1.50 },
            { name: "Mow the lawn 🌿", pay: 2 },
          ].map(t => (
            <button
              key={t.name}
              onClick={() => { setName(t.name); setPay(t.pay); setShowQuickAdd(false); }}
              style={{ fontSize: 12, padding: "4px 10px", borderRadius: 20, border: "1.5px solid #ff6a00", background: name === t.name ? "#ff6a00" : "#fff8f0", color: name === t.name ? "#fff" : "#ff6a00", cursor: "pointer", fontWeight: "600" }}
            >
              {t.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )}

  <input
    style={{
      width: "100%",
      boxSizing: "border-box",
      position: "relative",
      zIndex: 5
    }}
    placeholder="Chore name"
    value={name}
    onChange={e => setName(e.target.value)}
  />

  <input
  style={{
    width: "100%",
    boxSizing: "border-box",
    position: "relative",
    zIndex: 5
  }}
  placeholder="Pay (£)"
  type="number"
  value={pay}
  onChange={e => setPay(e.target.value)}
/>


  <select
    style={{ width: "100%", boxSizing: "border-box" }}
    value={type}
    onChange={e => setType(e.target.value)}
  >
    <option value="daily">Daily</option>
    <option value="weekly">Weekly</option>
    <option value="fortnightly">Fortnightly</option>
    <option value="once">One-off</option>
  </select>

  {type === "daily" && (
    <div style={{ fontSize: 12, color: "#ff6a00", background: "#fff8f0", border: "1px solid #ffd5b0", borderRadius: 8, padding: "8px 12px" }}>
      📅 <strong>Daily chore</strong> — this will appear every day for the child to complete.
    </div>
  )}

  {type === "weekly" && (
    <>
      <div style={{ fontSize: 12, color: "#1565c0", background: "#e3f2fd", border: "1px solid #90caf9", borderRadius: 8, padding: "8px 12px" }}>
        📆 <strong>Weekly chore</strong> — this will appear once a week on the day you choose below. Make sure you pick the right day!
      </div>
      <select
        style={{ width: "100%", boxSizing: "border-box" }}
        value={day}
        onChange={e => setDay(e.target.value)}
      >
        <option value="Mon">Mon</option>
        <option value="Tue">Tue</option>
        <option value="Wed">Wed</option>
        <option value="Thu">Thu</option>
        <option value="Fri">Fri</option>
        <option value="Sat">Sat</option>
        <option value="Sun">Sun</option>
      </select>
    </>
  )}

  {type === "fortnightly" && (
    <>
      <div style={{ fontSize: 12, color: "#6a1b9a", background: "#f3e5f5", border: "1px solid #ce93d8", borderRadius: 8, padding: "8px 12px" }}>
        🗓️ <strong>Fortnightly chore</strong> — pick a start date and it will repeat every two weeks on the same day.
      </div>
      <div style={{ fontSize: 13, fontWeight: "600", marginTop: 4 }}>Start date:</div>
      <input
        type="date"
        value={date}
        min={new Date().toISOString().split("T")[0]}
        onChange={e => setDate(e.target.value)}
        style={{ width: "100%", boxSizing: "border-box" }}
      />
    </>
  )}

  {type === "once" && (
    <div style={{ fontSize: 12, color: "#2e7d32", background: "#f1f8e9", border: "1px solid #a5d6a7", borderRadius: 8, padding: "8px 12px" }}>
      🗓️ <strong>One-off chore</strong> — this will appear once on the due date you select below and won't repeat.
    </div>
  )}

{type === "once" && (
  <div>
    <div style={{ fontSize: 13, fontWeight: "600", marginBottom: 4, opacity: 0.7 }}>📅 Due date</div>
    <input
      type="date"
      style={{
        width: "100%",
        boxSizing: "border-box",
        position: "relative",
        zIndex: 5,
        padding: "10px 12px",
        fontSize: 15,
        borderRadius: 10,
        border: "1px solid #ddd",
        appearance: "none",
        WebkitAppearance: "none"
      }}
      value={date}
      min={new Date().toISOString().split("T")[0]}
      onChange={e => setDate(e.target.value)}
    />
  </div>
)}

  {mode === "parent" && (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        onClick={() => setPhotoRequired(p => !p)}
        style={{
          display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
          padding: "10px 12px", borderRadius: 10,
          background: photoRequired ? "#fff8e1" : "#f9f9f9",
          border: `1.5px solid ${photoRequired ? "#ffb300" : "#bbb"}`
        }}
      >
        <div style={{
          width: 42, height: 24, borderRadius: 12,
          background: photoRequired ? "#ff6a00" : "#ddd",
          position: "relative", transition: "background 0.2s ease", flexShrink: 0
        }}>
          <div style={{
            position: "absolute", top: 3, left: photoRequired ? 21 : 3,
            width: 18, height: 18, borderRadius: "50%", background: "#fff",
            transition: "left 0.2s ease", boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
          }} />
        </div>
        <div>
          <div style={{ fontWeight: "600", fontSize: 14 }}>📸 Photo proof required</div>
          <div style={{ fontSize: 12, opacity: 0.6 }}>Child must upload a photo to complete</div>
        </div>
      </div>

      <div
        onClick={() => setLateDeduction(v => !v)}
        style={{
          display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
          padding: "10px 12px", borderRadius: 10,
          background: lateDeduction ? "#fff3e0" : "#f9f9f9",
          border: `1.5px solid ${lateDeduction ? "#ff9500" : "#bbb"}`
        }}
      >
        <div style={{
          width: 42, height: 24, borderRadius: 12, position: "relative",
          background: lateDeduction ? "linear-gradient(135deg,#ff4000,#ff9500)" : "#ddd",
          transition: "background 0.2s ease", flexShrink: 0
        }}>
          <div style={{
            position: "absolute", top: 3, left: lateDeduction ? 21 : 3,
            width: 18, height: 18, borderRadius: "50%", background: "#fff",
            transition: "left 0.2s ease", boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
          }} />
        </div>
        <div>
          <div style={{ fontWeight: "600", fontSize: 14 }}>⏰ 25% late deduction</div>
          <div style={{ fontSize: 12, opacity: 0.6 }}>Parent can dock 25% pay if done late</div>
        </div>
      </div>
    </div>
  )}

  <button style={styles.button} onClick={() => {
    const typeLabel = type === "daily" ? "a Daily chore (appears every day)" : type === "weekly" ? `a Weekly chore (appears every ${day})` : type === "fortnightly" ? `a Fortnightly chore (appears every other ${day})` : "a One-off chore (appears once on the selected date)";
    if (window.confirm(`Add "${name}" as ${typeLabel}?\n\nPay: £${pay}\n\nTap OK to confirm.`)) addChore();
  }}>
    Add Chore ➕
  </button>

</div>

    <div style={{ fontWeight: "700", fontSize: 17, marginBottom: 8, marginTop: 8 }}>
      📋 Today's Chores
    </div>

    {todayChores.length === 0 && (
      <div style={{ ...styles.cardColumn, textAlign: "center", padding: "24px 16px" }}>
        {mode === "child" ? (
          <>
            <div style={{ fontSize: 56, marginBottom: 10 }}>🎉</div>
            <div style={{ fontWeight: "700", fontSize: 17, marginBottom: 6 }}>No chores for today!</div>
            <div style={{ fontSize: 14, opacity: 0.55 }}>You're all caught up — enjoy your free time!</div>
          </>
        ) : chores.length === 0 ? (
          <>
            <div style={{ fontSize: 48, marginBottom: 12 }}>👋</div>
            <div style={{ fontWeight: "800", fontSize: 20, marginBottom: 6 }}>Welcome to Chore Ace!</div>
            <div style={{ fontSize: 14, color: "#666", marginBottom: 20 }}>Get set up in 3 simple steps:</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, textAlign: "left", marginBottom: 20 }}>
              {[
                { step: "1", icon: "👶", title: "Add your children", desc: "Go to Settings to add each child and set their PIN" },
                { step: "2", icon: "📋", title: "Add their chores", desc: "Use Quick Add above to add chores with suggested pay amounts" },
                { step: "3", icon: "✅", title: "Let them get started", desc: "Kids log in, complete chores, you approve and they earn money!" },
              ].map(s => (
                <div key={s.step} style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#fff8f0", borderRadius: 12, padding: "12px 14px", border: "1.5px solid #ffe0b2" }}>
                  <div style={{ fontSize: 28 }}>{s.icon}</div>
                  <div>
                    <div style={{ fontWeight: "700", fontSize: 14 }}>Step {s.step}: {s.title}</div>
                    <div style={{ fontSize: 13, color: "#666", marginTop: 2 }}>{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 13, color: "#ff6a00", fontWeight: "600" }}>💡 Tip: Use Quick Add above to get chores added in seconds!</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 56, marginBottom: 10 }}>🧹</div>
            <div style={{ fontWeight: "700", fontSize: 17, marginBottom: 6 }}>No chores today</div>
            <div style={{ fontSize: 14, opacity: 0.55 }}>Add a chore above to get started 👆</div>
          </>
        )}
      </div>
    )}

{todayChores.map(c => (
  <div key={c.id} style={{ ...styles.card, flexDirection: "column" }}>

    {editingChore === c.id ? (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <input
          placeholder="Chore name"
          value={editedName}
          onChange={e => setEditedName(e.target.value)}
        />
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="number"
            placeholder="Pay (£)"
            value={editedPay}
            onChange={e => setEditedPay(e.target.value)}
            style={{ flex: 1 }}
          />
          <button onClick={async () => {
            await updateDoc(doc(db, "chores", c.id), { name: editedName, pay: Number(editedPay) });
            setEditingChore(null);
          }}>✅</button>
          <button onClick={() => setEditingChore(null)}>✖️</button>
        </div>
      </div>
    ) : (
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          {c.name} — {fmtPay(c.pay)}
        </div>

        {mode === "parent" && (
          <button
            onClick={() => {
              setEditingChore(c.id);
              setEditedPay(c.pay);
              setEditedName(c.name);
            }}
          >
            ✏️
          </button>
        )}
      </div>
    )}

    <SwipeItem
      chore={c}
      isDone={isDoneThisPeriod(c)}
      isApproved={isApprovedThisPeriod(c)}
      complete={complete}
      remove={(chore, occurrenceDate) => {
        const isRecurring = chore.type === "daily" || chore.type === "weekly" || chore.type === "fortnightly";
        if (isRecurring) {
          setDeletePrompt({ id: chore.id, name: chore.name, type: chore.type, occurrenceDate });
        } else {
          if (window.confirm(`Delete "${chore.name}"? This cannot be undone.`)) remove(chore.id);
        }
      }}
      mode={mode}
      occurrenceDate={todayStr}
    />

  </div>
))}



</div>   {/* HOME tab */}


    {/* CALENDAR */}
<div
  style={{
    flex: "0 0 100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    overflowY: "auto",
    paddingBottom: 80,
    boxSizing: "border-box"
  }}
>

  {/* Child selector — parent with multiple children */}
  {mode === "parent" && children.length > 1 && (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "8px 16px 0", boxSizing: "border-box" }}>
      {children.map(c => (
        <button
          key={c.id}
          onClick={() => setActiveChild(c.id)}
          style={{
            padding: "6px 14px", borderRadius: 20, border: "none", cursor: "pointer",
            fontWeight: "600", fontSize: 14,
            background: c.id === activeChild ? "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)" : "#eee",
            color: c.id === activeChild ? "#fff" : "#333"
          }}
        >{c.name}</button>
      ))}
    </div>
  )}

   {weekDays.map(calDay => {

  // Use calDay (not 'day' which is a state variable for weekly chore picker)
  const calDayStr = new Date(calDay.getFullYear(), calDay.getMonth(), calDay.getDate()).toDateString();
  const calDow = calDay.getDay();
  const calMon = new Date(calDay.getFullYear(), calDay.getMonth(), calDay.getDate());
  calMon.setDate(calDay.getDate() - (calDow === 0 ? 6 : calDow - 1));
  const calMonStr = calMon.toDateString();

  const isDoneOnCalDay = (c) => {
    if (c.type === "daily") return c.completedDate === calDayStr;
    if (c.type === "weekly" || c.type === "fortnightly") return c.completedDate === calMonStr;
    return c.done;
  };

  const isApprovedOnCalDay = (c) => {
    if (c.type === "daily") return c.approvedDate === calDayStr;
    if (c.type === "weekly" || c.type === "fortnightly") return c.approvedDate === calMonStr;
    return c.approved;
  };

  const dayName = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][calDay.getDay()];

  const isToday = calDayStr === new Date().toDateString();

  const skippedChores = mode === "parent" ? chores.filter(c => {
    if (c.suggested || !activeChild || c.childId !== activeChild) return false;
    if (!c.skippedDates?.includes(calDayStr)) return false;
    if (c.type === "daily") return true;
    if (c.type === "weekly") return c.day === dayName;
    if (c.type === "fortnightly") {
      if (c.day !== dayName) return false;
      const start = c.startDate || 0;
      const diffDays = Math.round((new Date(calDay.getFullYear(), calDay.getMonth(), calDay.getDate()).getTime() - start) / (1000*60*60*24));
      return diffDays >= 0 && diffDays % 14 === 0;
    }
    return false;
  }) : [];

  const allChores = chores.filter(c => {
    if (c.suggested) return false;
    if (!activeChild) return false;
    if (c.childId !== activeChild) return false;
    if (c.skippedDates?.includes(calDayStr)) return false;
    if (c.createdAt) {
      const createdDay = new Date(c.createdAt);
      createdDay.setHours(0, 0, 0, 0);
      if (calDay < createdDay) return false;
    }
    if (c.type === "daily") return true;
    if (c.type === "once") {
      return new Date(c.date).toDateString() === calDayStr;
    }
    if (c.type === "weekly") {
      return c.day === dayName;
    }
    if (c.type === "fortnightly") {
      if (c.day !== dayName) return false;
      const start = c.startDate || 0;
      const diffDays = Math.round((new Date(calDay.getFullYear(), calDay.getMonth(), calDay.getDate()).getTime() - start) / (1000*60*60*24));
      return diffDays >= 0 && diffDays % 14 === 0;
    }
    return false;
  });

  return (
    <div
      key={calDayStr}
      style={{
        ...styles.dayCard,
        border: isToday ? "2px solid linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)" : "1px solid #e5e7eb"
      }}
    >

      {/* ✅ DATE HEADER */}
      <div style={{
        fontWeight: "700",
        marginBottom: 10,
        color: "#333",
        borderBottom: "1px solid #eee",
        paddingBottom: 6
      }}>
        {calDay.toLocaleDateString("en-GB", {
          weekday: "short",
          day: "numeric",
          month: "short"
        })}
      </div>

      {/* ✅ EMPTY STATE */}
      {allChores.length === 0 && (
        <div style={{
          textAlign: "center",
          padding: "32px 16px",
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
        }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>🗓️</div>
          <div style={{ fontWeight: "700", fontSize: 16, marginBottom: 6 }}>No chores scheduled</div>
          <div style={{ fontSize: 13, opacity: 0.55 }}>
            {mode === "parent"
              ? "Head to the home tab and add some chores to get started 👆"
              : "Your parent hasn't added any chores yet"}
          </div>
        </div>
      )}

      {/* ✅ CHORES */}
      {allChores.map(c => (
        <div
          key={c.id}
          style={{
            ...styles.card,
            flexDirection: "column",
            backgroundColor: isApprovedOnCalDay(c) ? "#d4edda" : isDoneOnCalDay(c) ? "#fff9db" : "#fff",
            borderLeft: mode === "child" ? `4px solid ${isApprovedOnCalDay(c) ? "#4caf50" : isDoneOnCalDay(c) ? "#f59e0b" : "#ddd"}` : undefined
          }}
        >
          {editingChore === c.id ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <input
                placeholder="Chore name"
                value={editedName}
                onChange={e => setEditedName(e.target.value)}
              />
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="number"
                  placeholder="Pay (£)"
                  value={editedPay}
                  onChange={e => setEditedPay(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button onClick={async () => {
                  await updateDoc(doc(db, "chores", c.id), { name: editedName, pay: Number(editedPay) });
                  setEditingChore(null);
                }}>✅</button>
                <button onClick={() => setEditingChore(null)}>✖️</button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div onClick={() => mode === "child" && !isDoneOnCalDay(c) && (setActiveCompletionDate(c.type === "daily" ? calDayStr : null), complete(c.id))} style={{ cursor: mode === "child" && !isDoneOnCalDay(c) ? "pointer" : "default", flex: 1 }}>
                {isDoneOnCalDay(c) ? (isApprovedOnCalDay(c) ? "💰" : "✅") : "⬜"} {c.name} — {fmtPay(c.pay)}
                {mode === "child" && (
                  <div style={{ fontSize: 11, marginTop: 2, fontWeight: "600", color: isApprovedOnCalDay(c) ? "#2e7d32" : isDoneOnCalDay(c) ? "#b45309" : "#999" }}>
                    {isApprovedOnCalDay(c) ? "Approved — money added! 💰" : isDoneOnCalDay(c) ? "Waiting for parent to approve" : "Tap to mark as done"}
                  </div>
                )}
              </div>
              {mode === "parent" && (
                <div style={{ display: "flex", gap: 6 }}>
                  {isApprovedOnCalDay(c) && (
                    <button onClick={() => { if (window.confirm(`Undo approval for "${c.name}"?`)) unapprove(c.id); }} style={{ background: "none", border: "none", fontSize: 15, cursor: "pointer", opacity: 0.6 }} title="Undo approval">↩️</button>
                  )}
                  {isDoneOnCalDay(c) && !isApprovedOnCalDay(c) && (
                    <button onClick={() => { if (window.confirm(`Reset "${c.name}" so the child can complete it again?`)) updateDoc(doc(db, "chores", c.id), { done: false, completedDate: null, photo: null, photoPath: null }); }} style={{ background: "none", border: "none", fontSize: 15, cursor: "pointer", opacity: 0.6 }} title="Reset chore">🔄</button>
                  )}
                  <button onClick={() => { setEditingChore(c.id); setEditedPay(c.pay); setEditedName(c.name); }} style={{ background: "none", border: "none", fontSize: 16, cursor: "pointer" }}>✏️</button>
                  <button onClick={() => {
                    const isRecurring = c.type === "daily" || c.type === "weekly" || c.type === "fortnightly";
                    if (isRecurring) {
                      setDeletePrompt({ id: c.id, name: c.name, type: c.type, occurrenceDate: calDayStr });
                    } else {
                      if (window.confirm(`Delete "${c.name}"? This cannot be undone.`)) remove(c.id);
                    }
                  }} style={{ background: "none", border: "none", fontSize: 16, cursor: "pointer", opacity: 0.5 }}>🗑</button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {skippedChores.map(c => (
        <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderRadius: 12, background: "#f5f5f5", marginTop: 6, opacity: 0.6 }}>
          <span style={{ fontSize: 14, textDecoration: "line-through", color: "#999" }}>⊘ {c.name} — {fmtPay(c.pay)}</span>
          <button
            onClick={async () => {
              await updateDoc(doc(db, "chores", c.id), { skippedDates: (c.skippedDates || []).filter(d => d !== calDayStr) });
            }}
            style={{ background: "none", border: "none", fontSize: 13, color: "#ff6a00", fontWeight: "700", cursor: "pointer", padding: "2px 6px" }}
            title="Restore this occurrence"
          >Undo</button>
        </div>
      ))}

    </div>
  );
})}
</div>

{/* ✅ REWARDS TAB (includes approvals at top for parents) */}
<div
  style={{
    flex: "0 0 100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    overflowY: "auto",
    paddingBottom: 80,
    boxSizing: "border-box"
  }}
>

  {/* APPROVALS SECTION — parent only */}
  {mode === "parent" && (
    <>
      <div style={{ fontWeight: "700", fontSize: 18, marginBottom: 4 }}>✅ Approvals</div>

      {chores.filter(c => c.suggested).length > 0 && (
        <>
          <div style={{ fontWeight: "600", fontSize: 14, marginTop: 10, marginBottom: 4, opacity: 0.7 }}>💡 Suggested chores</div>
          {chores.filter(c => c.suggested).map(c => (
            <div key={c.id} style={{ ...styles.cardColumn, borderLeft: "4px solid #f59e0b" }}>
              <div style={{ fontWeight: "600" }}>💡 {c.name} — {fmtPay(c.pay)}</div>
              <div style={{ fontSize: 13, opacity: 0.6 }}>Suggested by {children.find(ch => ch.id === c.childId)?.name || "child"}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button style={{ ...styles.button, marginTop: 0, flex: 1 }} onClick={() => updateDoc(doc(db, "chores", c.id), { suggested: false })}>Approve ✅</button>
                <button style={{ ...styles.button, marginTop: 0, flex: 1, background: "#ff4444" }} onClick={() => deleteDoc(doc(db, "chores", c.id))}>Reject ❌</button>
              </div>
            </div>
          ))}
        </>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, marginBottom: 4 }}>
        <div style={{ fontWeight: "600", fontSize: 14, opacity: 0.7 }}>✅ Completed chores</div>
      </div>
      {chores.filter(c => !c.suggested && c.done && !c.approved).length === 0 ? (
        <div style={{ marginTop: 4, opacity: 0.6, fontSize: 14, marginBottom: 8 }}>Nothing to approve yet</div>
      ) : (
        chores.filter(c => !c.suggested && c.done && !c.approved).map(c => {
          const choreChild = children.find(ch => ch.id === c.childId);
          const av = avatars.find(a => a.id === choreChild?.avatar);
          return (
          <div key={c.id} style={styles.cardColumn}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: "600" }}>✅ {c.name} — {fmtPay(c.pay)}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, opacity: 0.7 }}>
                {av && <span dangerouslySetInnerHTML={{ __html: applySkin(av.svg, SKIN_TONES.find(s => s.id === choreChild?.avatarSkin)?.hex) }} style={{ width: 22, height: 22, display: "inline-block" }} />}
                {choreChild?.name || "Unknown"}
              </div>
            </div>
            {c.photo && (
              <img
                src={c.photo}
                alt="proof"
                onClick={() => setPhotoLightbox(c.photo)}
                style={{ width: "100%", marginTop: 8, borderRadius: 8, cursor: "zoom-in" }}
              />
            )}
            {c.photo && <div style={{ fontSize: 11, opacity: 0.5, marginTop: 2 }}>Tap photo to enlarge</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <button style={{ ...styles.button, marginTop: 0, flex: 1 }} onClick={() => approve(c.id)}>Approve ✅</button>
              {c.lateDeduction && (
                <button style={{ ...styles.button, marginTop: 0, flex: 1, background: "linear-gradient(135deg,#f59e0b,#d97706)" }} onClick={() => { if (window.confirm(`Apply 25% late deduction?\n\n${fmtPay(c.pay)} → ${fmtPay(Math.round((c.pay||0)*0.75*100)/100)}`)) approve(c.id, true); }}>⏰ Late (-25%)</button>
              )}
              <button style={{ ...styles.button, marginTop: 0, flex: 1, background: "#ff4444" }} onClick={() => updateDoc(doc(db, "chores", c.id), { done: false, photo: null, photoPath: null, completedAt: null })}>Reject ❌</button>
            </div>
          </div>
          );
        })
      )}

      <div style={{ height: 1, background: "#eee", margin: "16px 0" }} />
    </>
  )}




    <div style={{ fontWeight: "700", fontSize: 18, marginBottom: 10 }}>
      🎁 Rewards
    </div>

    {rewardInputs.length === 0 && (
      <div style={{ ...styles.cardColumn, textAlign: "center", padding: "32px 16px" }}>
        <div style={{ fontSize: 48, marginBottom: 10 }}>🎁</div>
        <div style={{ fontWeight: "700", fontSize: 16, marginBottom: 6 }}>No rewards set yet</div>
        <div style={{ fontSize: 14, opacity: 0.55 }}>
          {mode === "parent"
            ? "Add rewards below — kids earn them by keeping their streak going!"
            : "Ask a parent to set up some rewards for you!"}
        </div>
      </div>
    )}

  {/* ✅ REWARDS LIST */}
  {rewardInputs.map((r, index) => {
    const isUnlocked = streak >= r.streak;
    const isClaimed = claimedRewards.includes(r.name);

    return (
      <div
        key={index}
        style={{
          padding: 12,
          borderRadius: 10,
          marginBottom: 8,
          background: isUnlocked ? "#e6ffed" : "#fff",
          border: isUnlocked ? "2px solid #52c41a" : "1px solid #eee"
        }}
      >
        {editRewards ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                value={r.name}
                onChange={e => {
                  const updated = [...rewardInputs];
                  updated[index] = { ...updated[index], name: e.target.value };
                  setRewardInputs(updated);
                }}
                style={{ flex: 1, padding: "6px 10px", borderRadius: 8, border: "1px solid #ddd", fontSize: 14 }}
                placeholder="Reward name"
              />
              <button
                onClick={() => setRewardInputs(rewardInputs.filter((_, i) => i !== index))}
                style={{ background: "#ffebee", border: "1px solid #ffcdd2", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontWeight: "700", color: "#e53935", fontSize: 16 }}
              >🗑</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, opacity: 0.7 }}>Streak days:</span>
              <input
                type="number"
                value={r.streak}
                min={1}
                onChange={e => {
                  const updated = [...rewardInputs];
                  updated[index] = { ...updated[index], streak: e.target.value === "" ? "" : parseInt(e.target.value) };
                  setRewardInputs(updated);
                }}
                onBlur={e => {
                  if (!e.target.value || parseInt(e.target.value) < 1) {
                    const updated = [...rewardInputs];
                    updated[index] = { ...updated[index], streak: 1 };
                    setRewardInputs(updated);
                  }
                }}
                style={{ width: 60, padding: "6px 8px", borderRadius: 8, border: "1px solid #ddd", fontSize: 14 }}
              />
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontWeight: "600" }}>{r.name}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              {isUnlocked
                ? isClaimed ? "✅ Claimed" : "🎉 Ready to claim!"
                : `${r.streak} days needed`}
            </div>
            {isUnlocked && !isClaimed && (
              <button
                onClick={async () => {
                  if (claimedRewards.includes(r.name)) return;
                  await updateDoc(doc(db, "children", activeChild), {
                    claimedRewards: [...claimedRewards, r.name]
                  });
                  setMessage(`🎉 ${r.name} unlocked! You're on fire 🔥`);
                  setShowTick(true);
                  setTimeout(() => setShowTick(false), 1500);
                  setTimeout(() => setMessage(""), 2000);
                }}
                style={{ marginTop: 8, background: "#52c41a", color: "#fff", border: "none", borderRadius: 8, padding: "6px 10px", cursor: "pointer" }}
              >Claim Reward 🎉</button>
            )}
          </>
        )}
      </div>
    );
  })}

{/* ✅ EDIT REWARDS BUTTONS — subscription gated */}
{mode === "parent" && (
  <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
    {isSubscribed ? (
      <>
        <button
          onClick={async () => {
            if (editRewards) {
              await setDoc(doc(db, "meta", `rewards_${familyId}`), { rewards: rewardInputs }, { merge: true });
              setMessage("✅ Rewards saved!");
              setTimeout(() => setMessage(""), 2000);
            }
            setEditRewards(!editRewards);
          }}
          style={{ ...styles.button, marginTop: 0, width: "100%", textAlign: "center", background: editRewards ? "linear-gradient(135deg,#43a047,#66bb6a)" : "linear-gradient(135deg,#ff4000,#ff6a00,#ff9500)" }}
        >
          {editRewards ? "Save Rewards ✅" : "Edit Rewards ⚙️"}
        </button>
        {editRewards && (
          <button
            onClick={() => setRewardInputs([...rewardInputs, { name: "New Reward", streak: 1 }])}
            style={{ ...styles.button, marginTop: 0, width: "100%", textAlign: "center", background: "#e8f5e9", color: "#2e7d32" }}
          >➕ Add Reward</button>
        )}
      </>
    ) : (
      <button
        onClick={() => setShowSubscribeModal(true)}
        style={{ ...styles.button, marginTop: 0, width: "100%", textAlign: "center" }}
      >⭐ Subscribe to Edit Rewards</button>
    )}
  </div>
)}
{false && (() => {
  const activeChore = chores.find(c => c.id === active);
  const needsPhoto = activeChore?.photoRequired && isSubscribed;
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>

        <div style={{ fontWeight: "700", fontSize: 16, marginBottom: 4 }}>
          {activeChore?.name}
        </div>

        {isSubscribed ? (
          <>
            {/* Hidden inputs — one for camera, one for gallery */}
            <input id="cameraInput" type="file" accept="image/*" capture="environment"
              style={{ display: "none" }}
              onChange={e => setSelectedFile(e.target.files[0])} />
            <input id="galleryInput" type="file" accept="image/*"
              style={{ display: "none" }}
              onChange={e => setSelectedFile(e.target.files[0])} />

            {!selectedFile ? (
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => document.getElementById("cameraInput").click()}
                  style={{ flex: 1, padding: "12px 8px", borderRadius: 12, border: "1.5px solid #ddd", background: "#f9f9f9", cursor: "pointer", fontWeight: "600", fontSize: 14 }}
                >📷 Camera</button>
                <button
                  onClick={() => document.getElementById("galleryInput").click()}
                  style={{ flex: 1, padding: "12px 8px", borderRadius: 12, border: "1.5px solid #ddd", background: "#f9f9f9", cursor: "pointer", fontWeight: "600", fontSize: 14 }}
                >🖼 Gallery</button>
              </div>
            ) : (
              <>
                <img src={URL.createObjectURL(selectedFile)} alt="preview"
                  style={{ width: "100%", borderRadius: 8 }} />
                <button
                  onClick={() => setSelectedFile(null)}
                  style={{ background: "none", border: "none", color: "#ff4444", cursor: "pointer", fontSize: 13, fontWeight: "600" }}
                >Remove ❌</button>
              </>
            )}

            {needsPhoto && !selectedFile && (
              <div style={{ fontSize: 13, color: "#f59e0b", fontWeight: "600", textAlign: "center" }}>
                📸 Photo required for this chore
              </div>
            )}
          </>
        ) : (
          activeChore?.photoRequired ? (
            <div
              onClick={() => { setShowModal(false); setShowSubscribeModal(true); }}
              style={{
                background: "#fff8e1", border: "2px dashed #ffb300", borderRadius: 12,
                padding: "16px", textAlign: "center", cursor: "pointer", marginBottom: 4
              }}
            >
              <div style={{ fontSize: 28, marginBottom: 6 }}>📸</div>
              <div style={{ fontWeight: "700", fontSize: 14 }}>Photo required — upgrade to add photos</div>
              <div style={{ marginTop: 10, color: "#ff6a00", fontWeight: "700", fontSize: 13 }}>Tap to upgrade ⭐</div>
            </div>
          ) : (
            <div
              onClick={() => { setShowModal(false); setShowSubscribeModal(true); }}
              style={{
                background: "#fff8e1", border: "2px dashed #ffb300", borderRadius: 12,
                padding: "12px", textAlign: "center", cursor: "pointer", marginBottom: 4
              }}
            >
              <div style={{ fontSize: 13, opacity: 0.7 }}>📸 Add photo proof (premium) — <span style={{ color: "#ff6a00", fontWeight: "700" }}>Upgrade ⭐</span></div>
            </div>
          )
        )}

        <button
          disabled={(needsPhoto && !selectedFile) || submitting}
          onClick={() => finish(active, isSubscribed ? selectedFile : null)}
          style={{
            ...styles.button, width: "100%", textAlign: "center", marginTop: 4,
            background: ((needsPhoto && !selectedFile) || submitting) ? "#ccc" : "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)",
            cursor: ((needsPhoto && !selectedFile) || submitting) ? "not-allowed" : "pointer"
          }}
        >
          {submitting ? "Saving..." : "Complete ✅"}
        </button>

        <button
          onClick={() => { setShowModal(false); setSelectedFile(null); }}
          style={{ background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 14 }}
        >Cancel</button>

      </div>
    </div>
  );
})()}

</div>   {/* rewards tab ✅ */}
{/* MONEY */}
<div
  style={{
    flex: "0 0 100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    overflowY: "auto",
    paddingBottom: 80,
    boxSizing: "border-box"
  }}
>

  {/* Child selector — parent only */}
  {mode === "parent" && children.length > 1 && (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
      {children.map(c => {
        const selected = (moneyViewChild || activeChild) === c.id;
        return (
          <button
            key={c.id}
            onClick={() => setMoneyViewChild(c.id)}
            style={{
              padding: "6px 14px", borderRadius: 20, border: "none", cursor: "pointer",
              fontWeight: "600", fontSize: 14,
              background: selected ? "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)" : "#eee",
              color: selected ? "#fff" : "#333"
            }}
          >{c.name}</button>
        );
      })}
    </div>
  )}

  {/* ✅ Balance */}
  {(() => {
    const viewChild = mode === "parent"
      ? children.find(c => c.id === (moneyViewChild || activeChild))
      : currentChild;
    return (
      <>
        <div style={{ fontSize: 14, opacity: 0.6, marginBottom: 2 }}>{viewChild?.name || "—"}</div>
        <div style={{ fontSize: 28, fontWeight: "bold" }}>£{viewChild?.balance || 0}</div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 14, opacity: 0.8 }}>
          <span>Pending</span>
          <strong>£{viewChild?.pending || 0}</strong>
        </div>
      </>
    );
  })()}
  {/* 📊 WEEKLY HISTORY */}
  <div style={{ fontWeight: "600", marginTop: 16, marginBottom: 8 }}>📊 Weekly Earnings</div>

  {/* THIS WEEK */}
  <div style={{ ...styles.cardColumn, borderLeft: "4px solid #ff6a00" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div>
        <div style={{ fontWeight: "700", fontSize: 15 }}>This week</div>
        <div style={{ fontSize: 12, opacity: 0.6 }}>
          {weekStart.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – {now.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
        </div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontWeight: "800", fontSize: 18, color: "#ff6a00" }}>£{thisWeekEarned}</div>
        <div style={{ fontSize: 12, opacity: 0.6 }}>earned</div>
      </div>
    </div>
    {potentialLeft > 0 && (
      <>
        <div style={{ height: 1, background: "#f0f0f0", margin: "10px 0" }} />
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
          <span style={{ opacity: 0.7 }}>🧾 Still to earn this week</span>
          <span style={{ fontWeight: "700", color: "#4CAF50" }}>£{potentialLeft}</span>
        </div>
        <div style={{ marginTop: 8, background: "#eee", borderRadius: 6, height: 8, overflow: "hidden" }}>
          <div style={{
            height: "100%",
            width: `${Math.min((thisWeekEarned / (thisWeekEarned + potentialLeft)) * 100, 100)}%`,
            background: "linear-gradient(135deg, #ff4000, #ff9500)",
            borderRadius: 6
          }} />
        </div>
        <div style={{ fontSize: 11, opacity: 0.5, marginTop: 4 }}>
          {Math.round((thisWeekEarned / (thisWeekEarned + potentialLeft)) * 100)}% of potential earned
        </div>
      </>
    )}
  </div>

  {/* 📅 DAILY BREAKDOWN — current week */}
  {(() => {
    const moneyChild = mode === "parent" ? (moneyViewChild || activeChild) : activeChild;
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    }).filter(d => d <= now);

    const dayTotals = days.map(d => {
      const dayStart = new Date(d); dayStart.setHours(0,0,0,0);
      const dayEnd = new Date(d); dayEnd.setHours(23,59,59,999);
      const total = chores
        .filter(c => {
          if (!c.done || !c.completedAt) return false;
          if (moneyChild && c.childId !== moneyChild) return false;
          const cd = new Date(c.completedAt);
          return cd >= dayStart && cd <= dayEnd;
        })
        .reduce((sum, c) => sum + (c.pay || 0), 0);
      return { d, total };
    }).filter(x => x.total > 0);

    if (dayTotals.length === 0) return null;
    return (
      <>
        <div style={{ fontWeight: "600", marginTop: 16, marginBottom: 8, fontSize: 14, opacity: 0.7 }}>Daily breakdown this week</div>
        {dayTotals.map(({ d, total }) => (
          <div key={d.toISOString()} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#fff8f0", borderRadius: 8, marginBottom: 6, borderLeft: "3px solid #ff9500" }}>
            <div style={{ fontSize: 14, fontWeight: "600" }}>{d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</div>
            <div style={{ fontSize: 15, fontWeight: "800", color: "#ff6a00" }}>£{total.toFixed(2)}</div>
          </div>
        ))}
      </>
    );
  })()}

  <div style={{ fontWeight: "600", marginTop: 16, marginBottom: 8, fontSize: 14, opacity: 0.7 }}>Previous weeks</div>
  {Array.from({ length: 5 }, (_, i) => {
    const moneyChild = mode === "parent" ? (moneyViewChild || activeChild) : activeChild;
    const wStart = new Date(weekStart);
    wStart.setDate(weekStart.getDate() - (i + 1) * 7);
    const wEnd = new Date(wStart);
    wEnd.setDate(wStart.getDate() + 7);

    const weekEarned = chores
      .filter(c => {
        if (!c.done || !c.completedAt) return false;
        if (moneyChild && c.childId !== moneyChild) return false;
        const d = new Date(c.completedAt);
        return d >= wStart && d < wEnd;
      })
      .reduce((sum, c) => sum + (c.pay || 0), 0);

    const choreCount = chores.filter(c => {
      if (!c.done || !c.completedAt) return false;
      if (moneyChild && c.childId !== moneyChild) return false;
      const d = new Date(c.completedAt);
      return d >= wStart && d < wEnd;
    }).length;

    return (
      <div key={i} style={{
        ...styles.cardColumn,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        borderLeft: weekEarned > 0 ? "4px solid #4CAF50" : "4px solid #ddd",
        opacity: weekEarned === 0 ? 0.6 : 1
      }}>
        <div>
          <div style={{ fontWeight: "600", fontSize: 14 }}>
            {wStart.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – {new Date(wEnd.getTime() - 1).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
          </div>
          <div style={{ fontSize: 12, opacity: 0.6 }}>
            {choreCount > 0 ? `${choreCount} chore${choreCount !== 1 ? "s" : ""} completed` : "Nothing completed"}
          </div>
        </div>
        <div style={{ fontWeight: "800", fontSize: 18, color: weekEarned > 0 ? "#2e7d32" : "#bbb" }}>
          £{weekEarned}
        </div>
      </div>
    );
  })}

  {/* ✅ Button — parent only */}
  {mode === "parent" && (() => {
    const payChild = children.find(c => c.id === (moneyViewChild || activeChild));
    const canPay = payChild && ((payChild.pending || 0) > 0 || (payChild.balance || 0) > 0);
    return showPayConfirm ? (
      <div style={{ background: "#fff3e0", border: "1px solid #ff6a00", borderRadius: 12, padding: 16, textAlign: "center" }}>
        <div style={{ fontWeight: "700", marginBottom: 8 }}>Pay out £{((payChild?.pending || 0) + (payChild?.balance || 0)).toFixed(2)} to {payChild?.name}?</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ ...styles.button, flex: 1, background: "#4CAF50" }} onClick={() => {
            updateDoc(doc(db, "children", payChild.id), {
              balance: 0,
              pending: 0
            }).then(() => {
              setShowPayConfirm(false);
              setMessage(`💸 Paid out £${((payChild.pending || 0) + (payChild.balance || 0)).toFixed(2)} to ${payChild.name}!`);
              setTimeout(() => setMessage(""), 3000);
            });
          }}>Confirm Pay Out</button>
          <button style={{ ...styles.button, flex: 1, background: "#999" }} onClick={() => setShowPayConfirm(false)}>Cancel</button>
        </div>
      </div>
    ) : (
      <button
        style={styles.button}
        onClick={() => canPay && setShowPayConfirm(true)}
        disabled={!canPay}
      >
        Pay Out
      </button>
    );
  })()}


</div>   {/* MONEY tab */}

{/* LEAGUES TAB */}
<div style={{
  flex: "0 0 100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  overflowY: "auto",
  paddingBottom: 80,
  padding: "16px 16px 80px",
  boxSizing: "border-box"
}}>
  <div style={{ fontWeight: "700", fontSize: 18, marginBottom: 16 }}>🏅 Leagues</div>

  {/* FAMILY LEADERBOARD */}
  {leaderboard.length > 0 && (
    <div style={{ background: "#fff", padding: 12, borderRadius: 14, marginBottom: 16, boxShadow: "0 4px 10px rgba(0,0,0,0.06)" }}>
      <div style={{ fontWeight: "700", marginBottom: 8 }}>🏆 Family Leaderboard</div>
      {leaderboard.map((child, index) => {
        let trophy = index === 0 ? "🏆✨" : index === 1 ? "🥈" : index === 2 ? "🥉" : "";
        return (
          <div key={child.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 10px", borderRadius: 10, marginBottom: 6, background: index === 0 ? "#fff7e6" : child.id === activeChild ? "#e6f7ff" : "#fff", border: child.id === activeChild ? "2px solid #ff6a00" : "1px solid #eee", fontWeight: "600" }}>
            <span>{trophy} {child.badge} {child.name}</span>
            <span>{child.points} pts</span>
          </div>
        );
      })}
      {leaderboard.every(c => c.points === 0) && (
        <div style={{ marginTop: 8, fontSize: 13, opacity: 0.6, textAlign: "center" }}>🚀 First chore completed takes the lead!</div>
      )}
    </div>
  )}

  {/* PENDING JOIN REQUESTS */}
  {leagueRequests.length > 0 && (
    <div style={{ ...styles.cardColumn, borderLeft: "4px solid #f59e0b", marginBottom: 8 }}>
      <div style={{ fontWeight: "700", marginBottom: 8 }}>📬 Join Requests</div>
      {leagueRequests.map(req => (
        <div key={req.id} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 14 }}>
            <strong>{req.childName}</strong> wants to join <strong>{req.leagueName}</strong>
          </div>
          {mode === "parent" ? (
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <button style={{ ...styles.button, marginTop: 0, flex: 1, fontSize: 13, padding: "8px 10px" }} onClick={() => approveLeagueRequest(req)}>Approve ✅</button>
              <button style={{ ...styles.button, marginTop: 0, flex: 1, fontSize: 13, padding: "8px 10px", background: "#ff4444" }} onClick={() => rejectLeagueRequest(req)}>Reject ❌</button>
            </div>
          ) : (
            <div style={{ marginTop: 6, fontSize: 13, color: "#f59e0b", fontWeight: "600" }}>⏳ Waiting for a parent to approve — ask mum or dad!</div>
          )}
        </div>
      ))}
    </div>
  )}

  {/* MY LEAGUES */}
  {leagues.length === 0 && leagueMembers.length === 0 && (
    <div style={{ ...styles.cardColumn, textAlign: "center", padding: "32px 16px", marginBottom: 16 }}>
      <div style={{ fontSize: 48, marginBottom: 10 }}>🏆</div>
      <div style={{ fontWeight: "700", fontSize: 16, marginBottom: 6 }}>No leagues yet</div>
      <div style={{ fontSize: 14, opacity: 0.55 }}>
        Create a league and challenge friends or family. Share your code to invite others!
      </div>
    </div>
  )}

  {/* LEAGUES I OWN */}
  {leagues.map(league => {
    const members = leagueMembers.filter(m => m.leagueId === league.id);

    const ranked = members.map(m => ({
      ...m,
      points: (m.weekStart === weekStart.toDateString() ? (m.weeklyPoints || 0) : 0)
    })).sort((a, b) => b.points - a.points);

    return (
      <div key={league.id} style={styles.cardColumn}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: "700", fontSize: 16 }}>🏆 {league.name}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {mode === "parent" && (
              <div style={{ fontSize: 12, background: "#f3f4f6", padding: "4px 10px", borderRadius: 20, fontFamily: "monospace", fontWeight: "700", letterSpacing: 1 }}>
                Code: {league.inviteCode}
              </div>
            )}
            <button onClick={() => {
              const shareMsg = `Join ${league.name} on Chore Ace! 🏆\n\nOpen the app: https://app.chore-ace.com\nGo to Leagues → Join a League\nEnter code: ${league.inviteCode}`;
              if (navigator.share) {
                navigator.share({ title: "Join my Chore Ace League!", text: shareMsg });
              } else {
                navigator.clipboard.writeText(shareMsg);
                setMessage("✅ Invite copied to clipboard!");
                setTimeout(() => setMessage(""), 2500);
              }
            }} style={{ background: "#e8f5e9", border: "1px solid #c8e6c9", borderRadius: 8, padding: "4px 8px", cursor: "pointer", fontSize: 13, fontWeight: "700", color: "#2e7d32" }}>📤 Invite</button>
            {mode === "parent" && (
              <button onClick={() => deleteLeague(league.id)} style={{ background: "#ffebee", border: "1px solid #ffcdd2", borderRadius: 8, padding: "4px 8px", cursor: "pointer", fontSize: 13, fontWeight: "700", color: "#e53935" }}>🗑</button>
            )}
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          {ranked.map((m, i) => {
            const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`;
            return (
              <div key={m.childId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 6px", borderRadius: 8, background: i === 0 ? "#fff7e6" : "transparent", marginBottom: 4 }}>
                <span>{medal} {m.childName}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: "700" }}>{m.points} pts</span>
                  {mode === "parent" && (
                    <button onClick={() => removeFromLeague(m.id)} style={{ fontSize: 11, padding: "2px 6px", borderRadius: 6, border: "1px solid #ddd", background: "#fff", cursor: "pointer", color: "#999" }}>Remove</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  })}

  {/* LEAGUES I'VE JOINED */}
  {[...new Set(leagueMembers.filter(m => m.leagueOwnerFamilyId !== familyId).map(m => m.leagueId))].map(leagueId => {
    const info = leagueMembers.find(m => m.leagueId === leagueId);
    const members = leagueMembers.filter(m => m.leagueId === leagueId);
    const ranked = members.map(m => ({
      ...m,
      points: (m.weekStart === weekStart.toDateString() ? (m.weeklyPoints || 0) : 0)
    })).sort((a, b) => b.points - a.points);

    return (
      <div key={leagueId} style={styles.cardColumn}>
        <div style={{ fontWeight: "700", fontSize: 16 }}>🏆 {info?.leagueName}</div>
        <div style={{ marginTop: 10 }}>
          {ranked.map((m, i) => {
            const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`;
            return (
              <div key={m.childId} style={{ display: "flex", justifyContent: "space-between", padding: "8px 6px", borderRadius: 8, background: i === 0 ? "#fff7e6" : "transparent", marginBottom: 4 }}>
                <span>{medal} {m.childName}</span>
                <span style={{ fontWeight: "700" }}>{m.points} pts</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  })}

  {/* ACTION BUTTONS — available to everyone */}
  <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
    <button style={{ ...styles.button, flex: 1, marginTop: 0, textAlign: "center" }} onClick={() => isSubscribed ? setShowCreateLeague(true) : setShowSubscribeModal(true)}>
      ➕ Create League
    </button>
    <button style={{ ...styles.button, flex: 1, marginTop: 0, textAlign: "center" }} onClick={() => (isSubscribed || mode === "child") ? setShowJoinLeague(true) : setShowSubscribeModal(true)}>
      🔗 Join League
    </button>
  </div>

  {/* REFERRAL LEADERBOARD */}
  {referralLeaderboard.length > 0 && (() => {
    const top10 = referralLeaderboard.slice(0, 10);
    const myRank = referralLeaderboard.findIndex(e => e.familyName === familyName);
    const myEntry = myRank >= 0 ? referralLeaderboard[myRank] : null;
    const myInTop10 = myRank >= 0 && myRank < 10;
    return (
      <div style={{ ...styles.cardColumn, marginTop: 16 }}>
        <div style={{ fontWeight: "700", fontSize: 16, marginBottom: 4 }}>🎁 Referral Champions</div>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>The family who refers the most friends each month wins 2 years free! 🏆 Get the whole family sharing your link — resets monthly from launch.</div>
        {top10.map((entry, i) => {
          const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`;
          const isMe = entry.familyName === familyName;
          return (
            <div key={i} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "8px 6px", borderRadius: 8, marginBottom: 4,
              background: isMe ? "#fff8e1" : i === 0 ? "#fff7e6" : "transparent",
              fontWeight: isMe ? "700" : "400"
            }}>
              <span>{medal} {entry.familyName}{isMe ? " (you)" : ""}</span>
              <span style={{ fontWeight: "700", color: "#ff6a00" }}>{entry.referralCount} referral{entry.referralCount !== 1 ? "s" : ""}</span>
            </div>
          );
        })}
        {myEntry && !myInTop10 && (
          <>
            <div style={{ textAlign: "center", color: "#ccc", fontSize: 18, margin: "4px 0" }}>•••</div>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "8px 6px", borderRadius: 8, background: "#fff8e1", fontWeight: "700"
            }}>
              <span>{myRank + 1}. {myEntry.familyName} (you)</span>
              <span style={{ fontWeight: "700", color: "#ff6a00" }}>{myEntry.referralCount} referral{myEntry.referralCount !== 1 ? "s" : ""}</span>
            </div>
          </>
        )}
      </div>
    );
  })()}

</div> {/* LEAGUES tab */}

</div>   {/* slider track */}
</div>   {/* slider wrapper */}

{/* ✅ NAV */}
<div style={styles.nav}>
  {/* HOME */}
  <button onClick={() => setTab("home")} style={{ ...styles.navButton, ...(tab === "home" ? styles.activeTab : {}) }}>
    <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
      <rect x="9" y="8" width="2.5" height="2.5" rx="0.4" fill="white" opacity="0.7"/>
      <rect x="12.5" y="8" width="2.5" height="2.5" rx="0.4" fill="white" opacity="0.7"/>
    </svg>
  </button>

  {/* CALENDAR */}
  <button onClick={() => setTab("calendar")} style={{ ...styles.navButton, ...(tab === "calendar" ? styles.activeTab : {}) }}>
    <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 3h-1V1h-2v2H8V1H6v2H5C3.9 3 3 3.9 3 5v16c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H5V9h14v12zM5 7V5h14v2H5z"/>
      <rect x="7" y="12" width="2" height="2" rx="0.3"/>
      <rect x="11" y="12" width="2" height="2" rx="0.3"/>
      <rect x="15" y="12" width="2" height="2" rx="0.3"/>
      <rect x="7" y="16" width="2" height="2" rx="0.3"/>
      <rect x="11" y="16" width="2" height="2" rx="0.3"/>
    </svg>
  </button>

  {/* REWARDS */}
  <button onClick={() => setTab("rewards")} style={{ ...styles.navButton, ...(tab === "rewards" ? styles.activeTab : {}), position: "relative" }}>
    <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
    </svg>
    {mode === "parent" && (chores.filter(c => (!c.suggested && c.done && !c.approved) || c.suggested).length > 0) && (
      <span style={{ position: "absolute", top: -2, right: -2, background: "#ff4444", color: "#fff", borderRadius: 10, fontSize: 10, fontWeight: "800", minWidth: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px" }}>
        {chores.filter(c => (!c.suggested && c.done && !c.approved) || c.suggested).length}
      </span>
    )}
  </button>

  {/* MONEY */}
  <button onClick={() => setTab("money")} style={{ ...styles.navButton, ...(tab === "money" ? styles.activeTab : {}) }}>
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
      <text x="4" y="20" fontSize="20" fontWeight="900" fontFamily="Georgia, serif" fill="currentColor">£</text>
    </svg>
  </button>

  {/* LEAGUES */}
  <button onClick={() => setTab("leagues")} style={{ ...styles.navButton, ...(tab === "leagues" ? styles.activeTab : {}) }}>
    <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 3h12v7c0 2.76-2.24 5-5 5h-2c-2.76 0-5-2.24-5-5V3z"/>
      <path d="M6 5.5H4.5C3.67 5.5 3 6.17 3 7v1c0 1.1.9 2 2 2H6V5.5z"/>
      <path d="M18 5.5h1.5C20.33 5.5 21 6.17 21 7v1c0 1.1-.9 2-2 2h-1V5.5z"/>
      <rect x="11" y="15" width="2" height="4"/>
      <rect x="8" y="19" width="8" height="2"/>
    </svg>
  </button>
</div>

  {/* FOOTER */}
  <div style={{ textAlign: "center", padding: "12px 0 20px", fontSize: 12, color: "#aaa" }}>
    <a href="/privacy.html" style={{ color: "#ff6a00", textDecoration: "none", marginRight: 12 }}>Privacy Policy</a>
    <a href="/terms.html" style={{ color: "#ff6a00", textDecoration: "none" }}>Terms &amp; Conditions</a>
    <div style={{ marginTop: 4 }}>© 2026 CC Project Controls Ltd</div>
  </div>

{showModal && createPortal((() => {
  const activeChore = chores.find(c => c.id === active);
  const effectivelySubscribed = isSubscribed || TESTER_FAMILY_IDS.includes(familyId);
  const needsPhoto = activeChore?.photoRequired && effectivelySubscribed;
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={{ fontWeight: "700", fontSize: 16, marginBottom: 4 }}>
          {activeChore?.name}
        </div>

        {effectivelySubscribed ? (
          <>
            <input id="cameraInput" type="file" accept="image/*" capture="environment"
              style={{ display: "none" }}
              onChange={e => setSelectedFile(e.target.files[0])} />
            <input id="galleryInput" type="file" accept="image/*"
              style={{ display: "none" }}
              onChange={e => setSelectedFile(e.target.files[0])} />

            {!selectedFile ? (
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => document.getElementById("cameraInput").click()}
                  style={{ flex: 1, padding: "12px 8px", borderRadius: 12, border: "1.5px solid #ddd", background: "#f9f9f9", cursor: "pointer", fontWeight: "600", fontSize: 14 }}
                >📷 Camera</button>
                <button
                  onClick={() => document.getElementById("galleryInput").click()}
                  style={{ flex: 1, padding: "12px 8px", borderRadius: 12, border: "1.5px solid #ddd", background: "#f9f9f9", cursor: "pointer", fontWeight: "600", fontSize: 14 }}
                >🖼 Gallery</button>
              </div>
            ) : (
              <>
                <img src={URL.createObjectURL(selectedFile)} alt="preview" style={{ width: "100%", borderRadius: 8 }} />
                <button onClick={() => setSelectedFile(null)} style={{ background: "none", border: "none", color: "#ff4444", cursor: "pointer", fontSize: 13, fontWeight: "600" }}>Remove ❌</button>
              </>
            )}

            {needsPhoto && !selectedFile && (
              <div style={{ fontSize: 13, color: "#f59e0b", fontWeight: "600", textAlign: "center" }}>
                📸 Photo required for this chore
              </div>
            )}
          </>
        ) : (
          activeChore?.photoRequired ? (
            <div onClick={() => { setShowModal(false); setShowSubscribeModal(true); }}
              style={{ background: "#fff8e1", border: "2px dashed #ffb300", borderRadius: 12, padding: "16px", textAlign: "center", cursor: "pointer", marginBottom: 4 }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>📸</div>
              <div style={{ fontWeight: "700", fontSize: 14 }}>Photo required — upgrade to add photos</div>
              <div style={{ marginTop: 10, color: "#ff6a00", fontWeight: "700", fontSize: 13 }}>Tap to upgrade ⭐</div>
            </div>
          ) : (
            <div onClick={() => { setShowModal(false); setShowSubscribeModal(true); }}
              style={{ background: "#fff8e1", border: "2px dashed #ffb300", borderRadius: 12, padding: "12px", textAlign: "center", cursor: "pointer", marginBottom: 4 }}>
              <div style={{ fontSize: 13, opacity: 0.7 }}>📸 Add photo proof (premium) — <span style={{ color: "#ff6a00", fontWeight: "700" }}>Upgrade ⭐</span></div>
            </div>
          )
        )}

        <button
          disabled={(needsPhoto && !selectedFile) || submitting}
          onClick={() => finish(active, effectivelySubscribed ? selectedFile : null)}
          style={{
            ...styles.button, width: "100%", textAlign: "center", marginTop: 4,
            background: ((needsPhoto && !selectedFile) || submitting) ? "#ccc" : "linear-gradient(135deg, #ff4000, #ff6a00, #ff9500)",
            cursor: ((needsPhoto && !selectedFile) || submitting) ? "not-allowed" : "pointer"
          }}
        >
          {submitting ? "Saving..." : "Complete ✅"}
        </button>

        <button onClick={() => { setShowModal(false); setSelectedFile(null); }}
          style={{ background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 14 }}>
          Cancel
        </button>
      </div>
    </div>
  );
})(), document.body)}

</div>   {/* container ✅ */}
</div>

);
}
